import axios from 'axios';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { imagePath, urls } from '../Url';
import storage from '../Assets/js/Global';
import { resetGuidelines } from "../utils/scanGuidelines";
import moment from 'moment';
import Loader from '../Components/Loader';
import useWhiteLabel from '../Assets/js/useWhiteLabel';
import { jwtDecode } from 'jwt-decode';
import { images } from '../Assets/images';
import { formatBiomarkerName, findNormalizedValue, normalizeScanDetails } from '../pages/scanDetails/scanNormalizer';
import { prefetchVoiceManifest, getVoiceFiles } from '../utils/voiceAudio';
import ScanIssueModal from '../modals/ScanIssueModal';

export const GlobalContext = createContext();
axios.defaults.withCredentials = true;

// Stamp every outgoing request with the current UI language so the backend can
// localize its response, without having to thread languageCode through each of
// the many individual API calls below by hand. Runs before per-call config, so a
// call that already sets its own languageCode keeps its own value.
axios.interceptors.request.use((config) => {
    const languageCode = storage.get('selectedLanguage') || 'en';

    if (config.data instanceof FormData) {
        if (!config.data.has('languageCode')) {
            config.data.append('languageCode', languageCode);
        }
    } else if (config.method?.toLowerCase() === 'get') {
        config.params = { languageCode, ...(config.params || {}) };
    } else if (
        !config.data ||
        (typeof config.data === 'object' && !(config.data instanceof URLSearchParams))
    ) {
        config.data = { languageCode, ...(config.data || {}) };
    }

    return config;
});

// Derive the company logo URLs from cached branding (if any) so logoUrl can be
// seeded synchronously on first render. This avoids the empty-string -> server-URL
// transition that makes every logo <img> load the bundled fallback first and then
// re-fetch the real logo (a visible flash + a duplicate network request).
const deriveStoredLogos = () => {
    try {
        const branding = storage.get('CompanyBranding');
        if (!branding) return { logo: '', header: '' };
        const logo = branding.company_logo ? imagePath + branding.company_logo : '';
        const header = branding.app_header_logo ? imagePath + branding.app_header_logo : '';
        return { logo, header: header || logo };
    } catch {
        return { logo: '', header: '' };
    }
};

// Version of the native face-scan SDK that produces the scanData we forward.
const SDK_VERSION = '3.2.1';

// 'ios' / 'android' mean the app is genuinely running inside the native RN
// WebView; anything else is 'web'. The bridge check has to come first because
// the user agent alone is not trustworthy - DevTools device emulation rewrites
// it, so a desktop browser in responsive mode reports an Android UA.
const getPlatform = () => {
    if (!window.ReactNativeWebView) return 'web';
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
    return 'web';
};

// navigator.platform reports the underlying kernel/arch (e.g. "Linux aarch64"
// on Android), not a friendly OS name, so derive it from the user agent instead.
const getOSName = () => {
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'Android';
    if (/iPad|iPhone|iPod/i.test(ua)) return 'iOS';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return navigator.platform || 'Unknown';
};

const BRAND_PATTERNS = [
    { re: /SM-|Galaxy/i, brand: 'Samsung' },
    { re: /Pixel/i, brand: 'Google' },
    { re: /Redmi|POCO|Xiaomi|Mi\s/i, brand: 'Xiaomi' },
    { re: /ONEPLUS/i, brand: 'OnePlus' },
    { re: /Moto|XT\d/i, brand: 'Motorola' },
    { re: /HUAWEI|HW-/i, brand: 'Huawei' },
    { re: /OPPO|CPH\d/i, brand: 'Oppo' },
    { re: /Vivo/i, brand: 'Vivo' },
    { re: /Nokia/i, brand: 'Nokia' },
    { re: /iPhone|iPad|iPod|Macintosh/i, brand: 'Apple' },
];

const guessBrand = (text) => BRAND_PATTERNS.find(({ re }) => re.test(text))?.brand || 'Unknown';

// Safari never exposes the specific iPhone model to the web. Each model does
// ship with one of a small, known set of CSS viewport sizes + DPR though, so
// matching against that narrows it to a family of candidates. Several
// generations reuse an identical screen (e.g. 14 Pro/15/15 Pro/16/16 Pro all
// share 393x852@3), so this is a best-effort guess, not a certainty — and it
// needs a new row whenever Apple ships a new screen size.
// Verified against webmobilefirst.com / yesviz.com (Aug 2026).
const IPHONE_SCREEN_MODELS = [
    { w: 320, h: 568, dpr: 2, models: 'iPhone 5/5c/5s/SE (1st gen)' },
    { w: 375, h: 667, dpr: 2, models: 'iPhone 6/6s/7/8/SE (2020/2022)' },
    { w: 414, h: 736, dpr: 3, models: 'iPhone 6 Plus/6s Plus/7 Plus/8 Plus' },
    { w: 375, h: 812, dpr: 3, models: 'iPhone X/XS/11 Pro' },
    { w: 414, h: 896, dpr: 2, models: 'iPhone XR/11' },
    { w: 414, h: 896, dpr: 3, models: 'iPhone XS Max/11 Pro Max' },
    { w: 360, h: 780, dpr: 3, models: 'iPhone 12 mini/13 mini' },
    { w: 390, h: 844, dpr: 3, models: 'iPhone 12/12 Pro/13/13 Pro/14' },
    { w: 428, h: 926, dpr: 3, models: 'iPhone 12 Pro Max/13 Pro Max/14 Plus' },
    { w: 393, h: 852, dpr: 3, models: 'iPhone 14 Pro/15/15 Pro/16/16 Pro' },
    { w: 430, h: 932, dpr: 3, models: 'iPhone 14 Pro Max/15 Plus/15 Pro Max/16 Plus' },
    { w: 440, h: 956, dpr: 3, models: 'iPhone 16 Pro Max/17 Pro Max' },
    { w: 402, h: 874, dpr: 3, models: 'iPhone 17/17 Pro' },
    { w: 420, h: 912, dpr: 3, models: 'iPhone 17 Air' },
];

const guessIphoneModel = () => {
    const w = Math.min(window.screen.width, window.screen.height);
    const h = Math.max(window.screen.width, window.screen.height);
    const dpr = window.devicePixelRatio || 1;
    return IPHONE_SCREEN_MODELS.find((m) => m.w === w && m.h === h && m.dpr === dpr)?.models || null;
};

// Browsers don't expose device brand/model directly. Chromium's User-Agent
// Client Hints can reveal the real Android model; everything else falls back
// to parsing the UA string (Android embeds the model, iOS/desktop don't).
const getDeviceBrandModel = async () => {
    if (navigator.userAgentData?.getHighEntropyValues) {
        try {
            const { model } = await navigator.userAgentData.getHighEntropyValues(['model']);
            if (model) return { brand: guessBrand(model), model };
        } catch {
            // fall through to UA parsing
        }
    }

    const ua = navigator.userAgent || '';
    const androidModel = ua.match(/Android[^;]*;\s*([^)]+)\)/i)?.[1]?.trim();
    if (androidModel) return { brand: guessBrand(androidModel), model: androidModel };

    if (/iPad/i.test(ua)) return { brand: 'Apple', model: 'iPad' };
    if (/iPhone|iPod/i.test(ua)) return { brand: 'Apple', model: guessIphoneModel() || 'iPhone' };
    if (/Macintosh/i.test(ua)) return { brand: 'Apple', model: 'Mac' };

    return { brand: 'Unknown', model: 'Unknown' };
};

export const GlobalProvider = ({ children }) => {

    const location = useLocation();
    let loginObject = storage.get('loginObj');
    let deviceInfo = storage.get('device_info');
    // Read device_info at CALL time, not render time. The native app posts it
    // asynchronously at WebView load, so anything that captured the value in a
    // closure (window.loginWithGoogle, for one) would hold the pre-arrival
    // empty value forever. Same pattern the surrounding code already uses for
    // DeviceId.
    const getDeviceData = () => storage.get('device_info') || '';
    const userId = loginObject?.userKey;
    let emailStored = storage.get('verifiedEmail');
    let userModule = storage.get('UserModule');
    const currentPath = location.pathname;
    let companyId = process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey;
    const userEmail = loginObject?.userEmail;
    const isLoggedIn = storage.get('isLoggedIn');
    const licenseType = storage.get('licenseType');
    const refreshTimerRef = useRef(null);
    const is4Life = process.env.REACT_APP_COMPANY_ID === '0it02f';

    const [redirection, setRedirection] = useState({
        productLink: "",
        productShare: ""
    });

    const navigate = useNavigate();
    // Loading state. Every request shares one overlay, so this has to count the
    // in-flight calls rather than hold a boolean: with a boolean the first
    // response to land clears the overlay while slower calls are still pending.
    // That is what hid the loader during /user/scan/create - posting the scan
    // runs alongside the mount-time fetches of the screen underneath it, and
    // whichever of those finished first switched the loader off.
    const [pendingRequests, setPendingRequests] = useState(0);
    const isLoading = pendingRequests > 0;
    const setIsLoading = useCallback((active) => {
        setPendingRequests((count) => (active ? count + 1 : Math.max(0, count - 1)));
    }, []);
    const [msgLoader, setMsgLoader] = useState(false);
    // null | "lowQuality" | "timeout" — which scan-issue popup variant is showing (if any)
    const [scanIssueModal, setScanIssueModal] = useState(null);
    const [isSessionRestoring, setIsSessionRestoring] = useState(false);
    const [timeZone, setTimeZone] = useState([]);
    const [globalUserEmail, setGlobalUserEmail] = useState(userEmail);
    const [scanSpectrum, setScanSpectrum] = useState({});
    const [scanSpectrumV2, setScanSpectrumV2] = useState({});

    const [showPasswords, setShowPasswords] = useState({
        field1: false,
        field2: false,
    });
    const [userData, setUserData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        otp: '',
        image: '',
        setPassword: '',
        confirmPassword: ''
    });
    const [userDatav2, setUserDatav2] = useState({
        email: '',
        firstName: '',
        lastName: '',
        image: '',
    });

    const [signupData, setSignupData] = useState(() => {
        const saved = storage.get('signupData');
        return saved || {
            firstName: '',
            lastName: '',
            email: '',
            secondaryEmail: '',
            phoneNumber: '',
            accessCode: '',
            age: '',
            weight: '',
            weightUnit: 'kg',
            height: '',
            heightUnit: 'cm',
            race: '',
            gender: '',
            waistCircumference: '',
            waistUnit: 'inch',
            diabetes: '',
            hypertension: '',
            smoking: '',
            country: '',
            time_zone: '',
            wellnessCategory: [],
            fruits: '',
            vegetables: '',
            grains: '',
            sugaryFoods: '',
            redMeat: '',
            fish: '',
            activity: '',
            password: '',
            confirmPassword: ''
        };
    });

    // const verifiedsecEmail = storage.get('verifiedSecondaryEmail');
    const verifiedsecEmail = loginObject?.userSecondaryEmail || storage.get('verifiedSecondaryEmail');
    const [email, setEmail] = useState(() => storage.get('verifiedEmail') || '');
    const [faqList, setFaqList] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [timer, setTimer] = useState(0);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [isThankYou, setIsThankYou] = useState(false);
    const [translatedLabels, setTranslatedLabels] = useState({});
    const [deviceId, setDeviceId] = useState(() => storage.get('DeviceId') || '');
    const [selected, setSelected] = useState(() => storage.get("selectedLanguage") || 'en');
    const [selectedBillType, setSelectedBillype] = useState(() => {
        const saved = storage.get("selectedBillingType");
        return saved ? Number(saved) : null;
    });
    const [userDetails, setUserDetails] = useState({})
    const [servings, setServings] = useState([]);
    const [isVerified, setIsVerified] = useState(false);
    const [logoUrl, setLogoUrl] = useState(() => deriveStoredLogos().logo);
    const [headerLogoUrl, setHeaderLogoUrl] = useState(() => deriveStoredLogos().header);
    const [isManualChange, setIsManualChange] = useState(false);
    const [showOTPSection, setShowOTPSection] = useState(false);
    const [documentData, setDocumentData] = useState({
        file: null,
        previewUrl: "",
        selectedCategory: "",
        documentID: null,
    });
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [documentContent, setDocumentContent] = useState({
        terms: '',
        privacy: '',
    });
    const [articlesList, setArticlesList] = useState([]);
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1,
        perPage: 10
    });
    const [search, setSearch] = useState("");
    const prevSearchRef = useRef("");
    const [articleDetails, setArticleDetails] = useState({});
    const [scanDetails, setScanDetails] = useState({});
    const [topSection, setTopSection] = useState(null);
    const [scanHistory, setScanHistory] = useState({})
    const [wellnessCat, setWellnessCat] = useState([])
    const [bioMarker, setBioMarker] = useState([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef(null);
    const [tempScanDetails, setTempScanDetails] = useState();
    const oauthPopupRef = useRef(null);
    const oauthSuccessTokenRef = useRef(null);
    // Guards against the OAuth popup's postMessage firing more than once for
    // the same successful login (seen when the popup redirects through
    // multiple pages) — without this, /webhook/signup gets called once per
    // duplicate message instead of once per login.
    const lastHandledOAuthTokenRef = useRef(null);
    const [historyDates, setHistoryDates] = useState([]);
    const [notification, setNotification] = useState([]);
    const [wellnessLabel, setWellnessLabel] = useState("");
    const [wellnessLabelNormal, setWellnessLabelNormal] = useState("");
    const [hasNotification, setHasNotification] = useState(false);
    const [isPlanActive, setIsPlanActive] = useState(storage.get('isPlanActive') || false);
    const [isNotificationReceived, setIsNotificationReceived] = useState();
    const [notificationsEnabled, setNotificationsEnabled] = useState();
    const [guestList, setGuestList] = useState([]);
    const [fourLifeScanHistory, setFourLifeScanHistory] = useState([]);
    const [scanLimit, setScanLimit] = useState(true);
    // Invitation/campaign quota — populated by getCampaignList from the
    // listCampaign API response. `loaded` lets screens tell "not fetched yet"
    // apart from a genuine zero so they don't warn/block prematurely.
    const [inviteStats, setInviteStats] = useState({
        total: 0,
        used: 0,
        remaining: 0,
        guestScanCount: 0,
        inviteScanCount: 0,
        loaded: false,
    });
    const [fourLifeuserData, setFourLifeUserData] = useState({
        userName: "",
        password: "",
    });
    const [isProfileUpdated, setIsProfileUpdated] = useState(false);
    const [products, setProducts] = useState({
        scanId: "",
        top4: [],
        remaining: [],
        shareLink: "https://example.com/recommendations/123",
    });
    const [fullProducts, setFullProducts] = useState({
        scanId: "",
        fullList: []
    })
    const [dynamicTypes, setDynamicTypes] = useState({});

    const prevLangRef = useRef(selected);
    useEffect(() => {
        storage.set("selectedLanguage", selected)
    }, [selected])

    // Guards the label-version check so overlapping route changes don't fire
    // multiple concurrent version fetches / label refetches.
    const labelVersionCheckRef = useRef(false);

    useEffect(() => {
        const handleOAuthMessage = async (event) => {

            const message = event?.data;
            if (!message || typeof message !== 'object') return;
            if (message?.type !== 'OAUTH_SUCCESS') return;
            if (!message?.token) {
                window.showToast?.('error', screenLabels?.oauth_token_missing?.label_value || 'OAuth token missing from popup');
                return;
            }
            if (lastHandledOAuthTokenRef.current === message.token) return;
            lastHandledOAuthTokenRef.current = message.token;

            const payload = {
                deviceID: deviceId || storage.get('DeviceId'),
                token: message.token,
                companyKey: companyId,
                device_data: deviceInfo || getDeviceData(),
            };

            oauthSuccessTokenRef.current = message.token;

            try {
                const webhookRes = await axios.post(urls.webhookSignup, payload);
                const res = webhookRes?.data;
                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;
                    const isBack = res?.is_back;
                    storage.set('isBack', isBack ? true : false);

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);
                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res?.email);
                    }

                    if (next === "dashboard") {
                        navigate(is4Life ? '/dashboard2' : '/dashboard');
                        // window.location.href = is4Life ? '/dashboard2' : '/dashboard';
                    } else if (next === "signup") {
                        setEmail(res?.email);
                        storage.set('verifiedEmail', res?.email);
                        updateSignupData("email", res?.email || '');
                        updateSignupData("firstName", res?.first_name || '');
                        updateSignupData("lastName", res?.last_name || '');
                        isBack ? navigate('/welcome-back') : navigate('/signup/account-details');
                    } else {
                        navigate('/login');
                    }

                } else {
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            } catch (err) {
                window.showToast('error', err?.response?.data?.error || err.message || screenLabels?.webhook_signup_failed?.label_value || 'Webhook signup failed');
            } finally {
                if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
                    oauthPopupRef.current.close();
                }
                oauthPopupRef.current = null;
            }
        };

        window.addEventListener('message', handleOAuthMessage);
        return () => {
            window.removeEventListener('message', handleOAuthMessage);
        };
    }, [deviceId,]);

    const screenLabels = translatedLabels?.common;
    useEffect(() => {
        setWellnessLabel(screenLabels?.avg_reading?.label_value || "Avg. Reading");
        // if (licenseType === "per_user_per_month") {
        // } else if (licenseType === "per_scan_per_month") {
        setWellnessLabelNormal(screenLabels?.normal_reading?.label_value || "Normal Reading");
        // }
    }, [screenLabels]);

    useEffect(() => {
        if (storage.get("isLoggedIn")) {
            storage.remove("signupData");
            storage.remove("signupStep")
            const loginObj = storage.get("loginObj");
            let userId = loginObj?.userKey;
            window.ReactNativeWebView?.postMessage(`userID|${userId}`);
        }
    }, [])

    useEffect(() => {
        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        }

        if (showCalendar) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showCalendar]);

    // Fire-and-forget: reports the outcome of a scan attempt (success or
    // cancelled/failed) to the backend. Never touches navigation/modal state.
    const logScanAttempt = useCallback((scanStatus, msg) => {
        let loginObject = storage.get('loginObj');
        let userId = loginObject?.userKey;
        let isGuest = storage.get("scanGuest") || false;
        let guestUserKey = storage.get("guestUserKey") || "";
        const payload = {
            userKey: userId,
            guestUserKey: isGuest ? guestUserKey : "",
            inviteUserKey: "",
            scanType: isGuest ? "guest" : "user",
            platform: getPlatform(),
            scanStatus,
            msg,
        };
        handleApiCall(
            (config) => axios.post(urls.logScanCancelled, payload, config),
            null,
            (error) => console.error("❌ Failed to log scan attempt", error),
            true,
            { silent: true }
        );
    }, []);

    const startScan = useCallback(
        async (scanData) => {
            let deviceId = storage.get('DeviceId');
            let loginObject = storage.get('loginObj')
            let userId = loginObject?.userKey;
            if (!deviceId || !scanData || !userId) return;
            let guestUserKey = storage.get("guestUserKey") || null;
            let isGuest = storage.get("scanGuest") || false;
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                scanData: { ...scanData, platform: getPlatform(), sdk_version: SDK_VERSION },
                guestUserKey: guestUserKey,
                scanType: isGuest ? "guest" : "user",
            };
            await handleApiCall(
                (config) => axios.post(urls.startScan, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        window.showToast('success', screenLabels?.scan_success?.label_value || "Scan completed");
                        logScanAttempt("success", "scan success");
                        if (isGuest) {
                            localStorage.setItem("scanId", res?.data?.scanKey);
                            navigate(is4Life ? '/user-scan-details-four-life' : '/user-scan-details');
                        } else {
                            navigate(is4Life ? '/dashboard2' : '/dashboard');
                        }
                    } else {
                        window.showToast("error", screenLabels?.scan_error?.label_value || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                },
                true
            );
        },
        [navigate, logScanAttempt]
    );

    // Reports why a scan was cancelled to the backend. Never touches
    // navigation/modal state — that flow stays exactly as-is.
    const logScanCancelled = useCallback((msg) => {
        const reason = msg?.reason || msg?.data?.reason || msg?.type || "Scan cancelled";
        logScanAttempt("fail", reason);
    }, [logScanAttempt]);

    useEffect(() => {
        const handler = (event) => {
            try {
                let msg = event?.data;
                let guestScan = storage.get("scanGuest") || false;
                if (typeof msg === "string") {
                    msg = JSON?.parse(msg);
                }
                if (msg && msg?.type === "finalValues") {
                    startScan(msg?.data);
                    setMsgLoader(false);
                } else if (msg?.type === "scanTimeout") {
                    setMsgLoader(false);
                    logScanCancelled(msg);
                    const path = location.pathname;
                    if (path === "/start-scan") navigate("/start-scan");
                    else if (path === "/guest-scan") navigate("/guest-scan");
                    else navigate(-1);
                    // Scan didn't complete — show the guidelines again next attempt.
                    resetGuidelines('scanGuidelinesSeen');
                    setScanIssueModal("timeout");
                }
                else if (msg?.type === "stopFaceScan") {
                    setMsgLoader(false);
                    logScanCancelled(msg);
                    const path = location.pathname;
                    if (path === "/start-scan") navigate("/start-scan");
                    else if (path === "/guest-scan") navigate("/guest-scan");
                    else navigate(-1);
                }
                else if (msg?.type === "lowQualityScan") {
                    // Not logged here — this always fires paired with a
                    // "stopFaceScan" message carrying the same reason, which
                    // already logs the attempt. Logging here too would double it.
                    setMsgLoader(false);
                    const path = location.pathname;
                    if (path === "/start-scan") navigate("/start-scan");
                    else if (path === "/guest-scan") navigate("/guest-scan");
                    else navigate(-1);
                    // Scan didn't complete — show the guidelines again next attempt.
                    resetGuidelines('scanGuidelinesSeen');
                    // unreliableData is the app's anti-spoof rejection: the same
                    // "Face Verification Unsuccessful" popup the web scan shows for it.
                    // issue picks the popup for a reason the way the web scan's
                    // showScanIssue(type, code) does: "measurementInconclusive"
                    // arrives with reason lowQuality once every visual check passed.
                    setScanIssueModal(
                        msg?.issue === "measurementInconclusive" ? "measurementInconclusive"
                            : msg?.reason === "blinkNotDetected" ? "blinkNotDetected"
                                : msg?.reason === "unreliableData" ? "verificationFailed"
                                    : "lowQuality"
                    );
                }
                else if (msg?.type === "cameraPermission") {
                    setMsgLoader(false);
                    navigate(is4Life ? '/dashboard2' : '/dashboard');
                    window.showToast("error", screenLabels?.camera_permission?.label_value || "Please allow camera access to use this feature.");
                }
            } catch (err) {
                console.error("❌ Failed to parse message", err, event?.data);
            } finally {

            }
        };

        document.addEventListener("message", handler);
        window.addEventListener("message", handler);

        return () => {
            document.removeEventListener("message", handler);
            window.removeEventListener("message", handler);
        };
    }, [startScan, navigate, logScanCancelled]);


    useEffect(() => {
        const handler = (event) => {
            try {
                let msg = event?.data;
                if (typeof msg === "string") {
                    msg = JSON?.parse(msg);
                }

                if (msg?.msg === "licence" && msg?.type) {
                    let message = msg?.type == "per_scan_per_month" ? "user" : "session";
                    storage.set("licenseType", message);
                }

                if (msg?.msg === "notification" && msg?.type === "reminder") {
                    setHasNotification(true);
                }

                // Device info pushed by the 4Life native app at WebView load
                // (App.tsx -> pushDeviceInfo). Nothing consumed this before, so
                // storage.get('device_info') was always empty and every
                // `device_data` we sent to the API went out blank — which is why
                // no FCM token or device row was ever recorded for app users.
                //
                // Stored in the snake_case shape the API expects: FcmModel
                // reads device_data.fcm_token / device_id / device_unique_id /
                // app_id / model / platform / os_version. The native payload is
                // camelCase, so it is mapped here rather than at each call site.
                if (msg?.type === "deviceInfo" && msg?.data) {
                    const d = msg.data;
                    storage.set('device_info', {
                        fcm_token: d.fcmToken || '',
                        device_id: d.deviceId || '',
                        // Native exposes a single unique id (DeviceInfo.getUniqueId());
                        // the API keeps two columns, so both are fed from it.
                        device_unique_id: d.deviceId || '',
                        // Best available app identifier from the native payload —
                        // it sends the app VERSION, not a bundle id.
                        app_id: d.appVersion || '',
                        model: d.model || '',
                        platform: d.platform || '',
                        os_version: d.systemVersion || '',
                        brand: d.brand || '',
                    });
                }
            } catch (err) {
                console.error("❌ Failed to parse licence message", err, event?.data);
            }
        };

        document.addEventListener("message", handler);
        window.addEventListener("message", handler);

        return () => {
            document.removeEventListener("message", handler);
            window.removeEventListener("message", handler);
        };
    }, []);

    // const handleApiCall = async (
    //     apiCall,
    //     successCallback,
    //     errorCallback,
    //     requiresAuth = true,
    //     options = {}
    // ) => {
    //     const { silent = false } = options;

    //     // if (isSessionRestoring && requiresAuth) {
    //     //     console.warn("API call blocked due to session restoration in progress");
    //     //     return null;
    //     // }

    //     if (!silent) {
    //         setIsLoading(true);
    //     }

    //     try {
    //         const freshToken = storage.get('accessToken');

    //         const config = requiresAuth
    //             ? { headers: { Authorization: `Bearer ${freshToken}` } }
    //             : {};

    //         const response = await apiCall(config);

    //         return successCallback ? successCallback(response) : response;

    //     } catch (error) {
    //         // window.showToast('error', error?.response?.data?.error || error.message || 'Something went wrong');
    //         if (error?.response?.status === 401) {
    //             await getRefreshToken();
    //             return null;
    //         }
    //         const backendError =
    //             error?.response?.data?.error || error?.message || 'Something went wrong';

    //         if (errorCallback) {
    //             errorCallback(error);
    //         } else {
    //             window.showToast('error', backendError);
    //         }

    //         return null;

    //     } finally {
    //         if (!silent) {
    //             setIsLoading(false);
    //         }
    //     }
    // };

    const handleApiCall = async (
        apiCall,
        successCallback,
        errorCallback,
        requiresAuth = true,
        options = {}
    ) => {
        const { silent = false, retry = true } = options;

        if (!silent) setIsLoading(true);

        try {
            const freshToken = storage.get('accessToken');

            const config = requiresAuth
                ? { headers: { Authorization: `Bearer ${freshToken}` } }
                : {};

            const response = await apiCall(config);

            return successCallback ? successCallback(response) : response;

        } catch (error) {

            if (error?.response?.status === 401 && retry) {
                console.log("Token expired → refreshing...");
                await getRefreshToken();
                return handleApiCall(
                    apiCall,
                    successCallback,
                    errorCallback,
                    requiresAuth,
                    { ...options, retry: false }
                );
            }

            const backendError =
                error?.response?.data?.error || error?.message || 'Something went wrong';

            if (errorCallback) {
                errorCallback(error);
            } else {
                window.showToast('error', backendError);
            }

            return null;

        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    const togglePassword = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const updateUserData = (field, value) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        setUserData({
            // Keep the pre-login email so Login stays prefilled after a reload.
            email: storage.get('verifiedEmail') || '',
            password: '',
            firstName: '',
            lastName: '',
            otp: '',
            image: '',
            setPassword: '',
            confirmPassword: ''
        });
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const id = storage.get('DeviceId');
            if (id) {
                setDeviceId(id);
                clearInterval(interval);
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);

    function getFontScale() {
        return window.devicePixelRatio || 1;
    }

    function getTimezoneDifference() {
        const offset = new Date().getTimezoneOffset();
        const hours = Math.floor(Math.abs(offset) / 60);
        const minutes = Math.abs(offset) % 60;
        const sign = offset <= 0 ? '+' : '-';
        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    const getDeviceId = useCallback(async () => {
        try {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            const { brand, model } = await getDeviceBrandModel();

            const payload = {
                os: getOSName(),
                device_name: navigator.userAgentData?.brands?.[0]?.brand ?? 'Google Chrome',
                system_version: navigator.userAgent,
                font_scale: getFontScale(),
                brand,
                model,
                screen_size: `${window.screen.width}x${window.screen.height}`,
                cpu: navigator.hardwareConcurrency || 'Unknown',

                ram: navigator.deviceMemory
                    ? navigator.deviceMemory >= 8
                        ? `${navigator.deviceMemory}+ GB`
                        : `${navigator.deviceMemory} GB`
                    : 'Unknown',

                network_type: connection?.effectiveType || 'Unknown',
                downlink: connection ? `${connection.downlink} Mbps` : 'Unknown',
                network_rtt: connection ? `${connection.rtt} ms` : 'Unknown',
                device_tz: getTimezoneDifference(),
            };
            await handleApiCall(
                () => axios.post(urls.getDeviceId, payload),
                (response) => {
                    if (response.data.status === true) {
                        storage.set('DeviceId', response.data.deviceID);
                    } else {
                        console.error('API Error:', response.data);
                    }
                },
                (error) => {
                    window.showToast('error', error);
                },
                false
            );
        } catch (error) {
            window.showToast('error', error);
        }
    }, []);

    const getCompanyBranding = useCallback(async () => {
        if (!deviceId) return;
        // Public campaign scan routes don't need company branding.
        if (window.location.pathname.startsWith('/campaign/')) return;
        const existingBranding = storage.get("CompanyBranding");

        if (existingBranding) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId
        };

        await handleApiCall(
            (config) => axios.post(urls.getCompanyDetails, payload, { ...config, skipLogoutInterceptor: true }),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    storage.set("CompanyBranding", res.list);
                }
            },
            (error) => window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'),
            false
        );

    }, [deviceId]);

    const checkUserRegistration = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!deviceId) return;
        if (!userData?.email) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return;
        }

        if (!emailRegex.test(userData?.email)) {
            window.showToast('error', screenLabels?.valid_email_required?.label_value || 'Please enter a valid email address');
            return;
        }
        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            email: userData?.email?.toLowerCase()
        };

        setEmail(userData?.email);

        await handleApiCall(
            () => axios.post(urls.checkUserRegistration, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    storage.set('UserModule', res.module);
                    storage.set('verifiedEmail', userData?.email);
                    if (res.module === 'Signup') {
                        navigate('/verify-otp');
                    } else if (res.module === 'Login') {
                        navigate('/password')
                    }
                    updateUserData('email', '')
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('email', '')
                }
            },
            (error) => {
                updateUserData('email', '')
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            false
        );
    };

    const fourLifeSSOLogin = async () => {
        const response = await handleApiCall(
            () => axios.post(urls.ssoLogin, { device_data: getDeviceData() }),
            (response) => response,
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.sso_login_failed?.label_value || 'SSO login failed');
                return null;
            },
            false
        );

        const res = response?.data;
        if (!res || res.status !== true || !res.url) {
            window.showToast('error', res?.error || screenLabels?.sso_login_failed?.label_value || 'SSO login failed');
            return;
        }

        const popup = window.open(res.url, 'oauthPopup', 'width=600,height=700,toolbar=0,menubar=0,location=1,resizable=1,scrollbars=1,status=1');
        // if (!popup) {
        //     window.showToast('error', 'Popup blocked. Please allow popups and try again.');
        //     return;
        // }

        // oauthPopupRef.current = popup;
        // storage.set("secret", res.secret || '');

        // const closeWatcher = setInterval(async () => {
        //     if (popup.closed) {
        //         clearInterval(closeWatcher);
        //         oauthPopupRef.current = null;
        //         window.showToast('info', 'SSO window closed.');

        //         const secret = storage.get('secret') || '';
        //         if (!secret) {
        //             window.showToast('error', 'SSO secret token missing after close.');
        //             return;
        //         }

        //         try {
        //             const payload = {
        //                 deviceID: deviceId || storage.get('DeviceId'),
        //                 secret,
        //                 companyKey: companyId
        //             };
        //             const webhookRes = await axios.post(urls.webhookSignup, payload);
        //             const webhookData = webhookRes?.data;

        //             if (webhookData?.status === true) {
        //                 if (webhookData?.msg) window.showToast('success', webhookData.msg);

        //                 const auth = webhookData.auth;
        //                 const next = webhookData.next;

        //                 if (auth && typeof auth === 'object' && auth.access_token) {
        //                     const decodeAccessToken = jwtDecode(auth.access_token);
        //                     storage.set('accessToken', auth.access_token);
        //                     storage.set('refreshToken', auth.refresh_token);
        //                     storage.set('tokenExpiry', decodeAccessToken?.exp);
        //                     storage.set('loginObj', decodeAccessToken);
        //                     storage.set('isLoggedIn', true);
        //                     window.ReactNativeWebView?.postMessage(`userID|${decodeAccessToken?.userKey}`);
        //                     startTokenRefreshTimer();
        //                 }

        //                 if (next === 'dashboard') {
        //                     window.location.href = is4Life ? '/dashboard2' : '/dashboard';
        //                 } else if (next === 'signup') {
        //                     setEmail(webhookData?.email);
        //                     storage.set('verifiedEmail', webhookData?.email);
        //                     updateSignupData('email', webhookData?.email || '');
        //                     updateSignupData('firstName', webhookData?.first_name || '');
        //                     updateSignupData('lastName', webhookData?.last_name || '');
        //                     navigate('/signup/account-details');
        //                 } else {
        //                     window.location.href = '/login';
        //                 }
        //             } else {
        //                 window.showToast('error', webhookData?.error || 'Webhook signup failed after SSO close.');
        //             }
        //         } catch (err) {
        //             window.showToast('error', err?.response?.data?.error || err.message || 'Webhook call on popup close failed.');
        //         }
        //     }
        // }, 500);
    };

    const getuserDetails = async (deviceId, email) => {
        const payload = {
            deviceID: deviceId,
            email: email
        };

        setEmail(email);

        await handleApiCall(
            () => axios.post(urls.getuserDetails, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    setUserDatav2({
                        email: res.userDetails.email || '',
                        firstName: res.userDetails.first_name || '',
                        lastName: res.userDetails.last_name || '',
                        image: res.userDetails.profile_photo || '',
                    });

                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('email', '')
                }
            },
            (error) => {
                updateUserData('email', '')
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            false
        );
    };

    const loginByPassword = async () => {
        if (!deviceId) return;
        if (!userData?.password) {
            window.showToast('error', screenLabels?.password_required?.label_value || 'Password is required');
            return;
        }
        const payload = {
            deviceID: deviceId,
            email: emailStored.toLowerCase(),
            password: userData?.password,
            licence_type: licenseType,
            company_id: process.env.REACT_APP_COMPANY_ID,
            device_data: getDeviceData()
        };

        await handleApiCall(
            () => axios.post(urls.userLogin, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    storage.set('isLoggedIn', true)
                    const decodeAccessToken = jwtDecode(res?.token?.access_token);

                    storage.set('accessToken', res?.token?.access_token);
                    storage.set('refreshToken', res?.token?.refresh_token);
                    storage.set('tokenExpiry', decodeAccessToken?.exp);
                    startTokenRefreshTimer();
                    storage.set('loginObj', decodeAccessToken)
                    storage.remove('verifiedEmail');

                    navigate(is4Life ? '/dashboard2' : '/dashboard');
                    storage.set('UserModule', 'Login')
                    let userId = decodeAccessToken?.userKey;
                    window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                    updateUserData('password', '')
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('password', '')
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('password', '')
            },
            false
        );
    };

    const sendEmailOTP = async () => {
        if (!deviceId) return;
        if (!email && !loginObject?.userEmail && !emailStored) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase() || loginObject?.userEmail?.toLowerCase() || emailStored.toLowerCase(),
            module: userModule,
            companyKey: companyId,
            role: loginObject?.role || 3
        };
        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    storage.set('verifiedEmail', email);
                    window.showToast('success', res.msg);
                    setShowOTPSection(true)
                    if (currentPath === '/change-password') {
                        navigate('/change-password')
                    } else if (storage.get("isLoggedIn") ? navigate('/deleteAcc-one') : storage.get('UserModule') == 'deleteUser' ? setShowOTPSection(true) : navigate('/verify-otp'))
                        email = '';
                    updateUserData('otp', '');
                    return true;
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    email = '';
                    updateUserData('otp', '');
                    return false;
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                email = '';
                return false;
            },
            isLoggedIn ? true : false
        );
    };

    const fourLifeEmailOTP = async ({ email, module }) => {
        if (!deviceId) return;

        if (!email) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase(),
            module: module,
            companyKey: companyId,
            role: loginObject?.role || 3
        };
        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    storage.set('verifiedSecondaryEmail', email);
                    window.showToast('success', res.msg);

                    updateUserData('otp', '');
                    return true;
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    email = '';
                    updateUserData('otp', '');
                    return false;
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                email = '';
                return false;
            },
            isLoggedIn ? true : false
        );
    };
    const resendEmailOTP = async () => {
        if (!deviceId) return;
        if (!email && !loginObject?.userEmail && !emailStored) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase() || loginObject?.userEmail?.toLowerCase() || emailStored?.toLowerCase(),
            module: userModule,
            role: loginObject?.role || 3,
            companyKey: process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey,
        };

        storage.set('verifiedEmail', email);

        await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', screenLabels?.otp_resent_successfully?.label_value || 'OTP Resent successfully.');
                    startResendTimer();
                    updateUserData('otp', '');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('otp', '');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('otp', '');
            },
            isLoggedIn ? true : false
        );
    };
    const fourLiferesendOTP = async ({ email, module }) => {
        if (!deviceId) return;

        if (!email) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase(),
            module: module,
            role: loginObject?.role || 3,
            companyKey: process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey
        };

        storage.set('verifiedSecondaryEmail', email);

        await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', screenLabels?.otp_resent_successfully?.label_value || 'OTP Resent successfully.');
                    startResendTimer();
                    updateUserData('otp', '');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('otp', '');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('otp', '');
            },
            isLoggedIn ? true : false
        );
    };
    const startResendTimer = () => {
        setTimer(60);
        setResendDisabled(true);
    };

    const handlenavigateToEmail = () => {
        storage.set('UserModule', 'ForgetPassword');
        navigate('/forgot-password');
    };

    const handleChangePassword = () => {
        storage.set('UserModule', 'changePassword');
        navigate('/change-password');
    }

    const handleDeletAccount = () => {
        storage.set('UserModule', 'deleteUser');
        navigate('/deleteAcc-one');
    }
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
        } else {
            setResendDisabled(false);
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const verifyOTP = async () => {
        if (!deviceId) return;
        if (!userData?.otp) {
            window.showToast('error', screenLabels?.otp_required?.label_value || 'OTP is required');
            return;
        }
        const payload = {
            deviceID: deviceId,
            email: emailStored || userEmail || email,
            otp: userData?.otp,
            module: userModule,
            role: loginObject?.role || 3,
            companyKey: companyId
        };

        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.verifyOTP, payload, config) : () => axios.post(urls.verifyOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setSignupData({});
                    storage.remove('signupData')
                    setIsVerified(true)
                    window.showToast('success', res.msg);
                    if (currentPath === '/change-password') {
                        navigate('/change-password')
                    } else if (storage.get("isLoggedIn") ? navigate('/deleteAcc-one') : storage.get('UserModule') == 'deleteUser' ? setShowOTPSection(false) : navigate('/reset-password'));

                    if (userModule === "Signup") {
                        navigate('/signup/account-details');
                    } else {
                        getuserDetails(payload.deviceID, payload.email)
                    }
                    updateUserData('otp', '');
                    return true
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('otp', '');
                    return false;
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('otp', '');
                return false;
            },
            isLoggedIn ? true : false
        );
    };

    const fourLifeVerifyOTP = async ({ secondaryemail, module }) => {
        if (!deviceId) return;
        if (!userData?.otp) {
            window.showToast('error', screenLabels?.otp_required?.label_value || 'OTP is required');
            return;
        }
        const payload = {
            deviceID: deviceId,
            email: secondaryemail,
            otp: userData?.otp,
            module: module,
            role: loginObject?.role || 3,
            companyKey: companyId
        };

        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.verifyOTP, payload, config) : () => axios.post(urls.verifyOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setIsVerified(true)
                    window.showToast('success', res.msg);
                    updateUserData("otp", "");
                    return res;

                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('otp', '');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('otp', '');
            },
            isLoggedIn ? true : false
        );
    };

    const resetPassword = async () => {
        if (!deviceId) return;
        if (userData?.setPassword !== userData?.confirmPassword) {
            window.showToast('error', screenLabels?.passwords_do_not_match?.label_value || 'Password and confirm password do not match')
            return;
        }
        const payload = {
            deviceID: deviceId,
            password: userData?.setPassword,
            confirm_password: userData?.confirmPassword,
            email: emailStored?.toLowerCase() || userEmail || email,
            module: userModule,
            role: loginObject?.role || 3
        };

        await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.resetPassword, payload, config) : () => axios.post(urls.resetPassword, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', res.msg)
                    navigate('/profile');
                    updateUserData('setPassword', '');
                    updateUserData('confirmPassword', '');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            isLoggedIn ? true : false
        );
    };
    const isLoggingOutRef = useRef(false);

    const doLogout = useCallback(async (redirect = true) => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;

        let accessToken = storage.get('accessToken');

        try {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            const email = loginObject?.userEmail;

            if (!email || !deviceId || !userId) return;

            const payload = {
                deviceID: deviceId,
                email: email.toLowerCase() || userEmail,
                userID: userId,
                role: 3,
                token: accessToken
            };

            await handleApiCall(
                () => axios.post(urls.doLogout, payload),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        const selectedLanguage = storage.get('selectedLanguage');
                        const translatedLabels = storage.get('appLabels');
                        const isBiometricEnabled = storage.get("biometricAuthEnabled");
                        const deviceData = storage.get("device_info");

                        storage.clear();

                        storage.set('DeviceId', deviceId);
                        storage.set('device_info', deviceData);
                        storage.set('licenseType', licenseType);
                        storage.set('appLabels', translatedLabels);
                        storage.set('selectedLanguage', selectedLanguage);
                        if (isBiometricEnabled !== null && isBiometricEnabled !== undefined) {
                            storage.set('biometricAuthEnabled', isBiometricEnabled);
                        }
                        window?.ReactNativeWebView?.postMessage("googlesignOut");
                        getCompanyBranding();
                        if (redirect) {
                            navigate('/login');
                            // window.location.href = '/login';
                        }
                    } else {
                        window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    }
                },
                (error) => {
                    window.showToast(
                        'error',
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                    );
                },
                true
            );
        } finally {
            // Reset after a delay to allow navigation to complete
            setTimeout(() => {
                isLoggingOutRef.current = false;
            }, 3000);
        }
    }, [loginObject?.userEmail, deviceId, userEmail, userId, getCompanyBranding, navigate]);


    const refreshPromiseRef = useRef(null);
    const isRefreshingRef = useRef(false);

    const getRefreshToken = useCallback(async () => {
        if (!deviceId) throw new Error("Device ID missing");

        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        refreshPromiseRef.current = (async () => {
            try {
                const refresh_token = storage.get("refreshToken");

                if (!refresh_token) return;

                const payload = {
                    deviceID: deviceId,
                    refreshToken: refresh_token
                };

                const res = await axios.post(urls.getRefreshToken, payload, {
                    skipLogoutInterceptor: true
                });

                const data = res.data;

                if (!data.status) {
                    throw new Error(data.error || "Refresh failed");
                }
                setIsSessionRestoring(true);
                const accessToken = data?.auth?.access_token;
                const refreshToken = data?.auth?.refresh_token;

                const decoded = jwtDecode(accessToken);

                // ✅ STORE
                storage.set("accessToken", accessToken);
                storage.set("refreshToken", refreshToken);
                storage.set("tokenExpiry", decoded.exp);

                // ✅ UPDATE AXIOS DEFAULT HEADER (IMPORTANT)
                // axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

                return accessToken;

            } catch (err) {
                window.showToast('error', err?.response?.data?.error || err.message || screenLabels?.session_refresh_failed?.label_value || 'Session refresh failed');
                throw err;
            } finally {
                refreshPromiseRef.current = null;
            }
        })();

        return refreshPromiseRef.current;

    }, [deviceId]);

    const startTokenRefreshTimer = useCallback(() => {
        const expiry = storage.get("tokenExpiry");
        if (!expiry) return;

        const now = Math.floor(Date.now() / 1000);
        const timeLeft = expiry - now;

        const refreshTime = Math.max((timeLeft - 30) * 1000, 0);

        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        refreshTimerRef.current = setTimeout(async () => {
            try {
                await getRefreshToken();
                startTokenRefreshTimer();
            } catch {
                doLogout();
            }
        }, refreshTime);

    }, [getRefreshToken, doLogout]);

    const restoreSession = useCallback(async (isColdStart = false) => {
        if (isRefreshingRef.current) return;

        const accessToken = storage.get('accessToken');
        const refreshToken = storage.get('refreshToken');
        const tokenExpiry = storage.get('tokenExpiry');
        const isLoggedIn = storage.get('isLoggedIn');

        if (!accessToken || !refreshToken || !tokenExpiry || !isLoggedIn) {
            return;
        }

        const currentTime = Math.floor(Date.now() / 1000);
        // Refresh if expired or expiring in less than 30 seconds
        if (currentTime >= (tokenExpiry - 30)) {
            setIsSessionRestoring(true);
            try {
                await getRefreshToken();
                console.log("Token restored successfully");
                startTokenRefreshTimer(); // Ensure timer starts after restoration
            } catch (error) {
                console.error("Session restoration failed:", error);
                doLogout();
            } finally {
                setIsSessionRestoring(false);
            }
        } else if (isColdStart) {
            startTokenRefreshTimer();
        }
    }, [getRefreshToken, doLogout, startTokenRefreshTimer]);

    useEffect(() => {
        const handleAppResume = () => {
            if (document.visibilityState === 'visible') {
                restoreSession();
            }
        };

        // Cold start check
        restoreSession(true);

        document.addEventListener('visibilitychange', handleAppResume);
        return () => {
            document.removeEventListener('visibilitychange', handleAppResume);
        };
    }, [restoreSession]);

    // useEffect(() => {
    //     getScanDetails();
    //     const handleFocus = () => {
    //         console.log("Focus event");
    //         getScanDetails();
    //     };

    //     const handleVisibility = () => {
    //         if (document.visibilityState === "visible") {
    //             console.log("Visibility event");
    //             getScanDetails();
    //         }
    //     };

    //     window.addEventListener("focus", handleFocus);
    //     document.addEventListener("visibilitychange", handleVisibility);

    //     return () => {
    //         window.removeEventListener("focus", handleFocus);
    //         document.removeEventListener("visibilitychange", handleVisibility);
    //     };
    // }, []);

    useEffect(() => {
        if (storage.get('isLoggedIn')) {
            startTokenRefreshTimer();
        }
    }, [startTokenRefreshTimer]);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,

            async (error) => {

                if (error.config?.skipLogoutInterceptor) {
                    return Promise.reject(error);
                }

                const status = error.response?.status;

                if (status === 423) {
                    if (!isLoggingOutRef.current) {
                        isLoggingOutRef.current = true;
                        storage.clear();
                        window?.ReactNativeWebView?.postMessage("googlesignOut");
                        getDeviceId();
                        getCompanyBranding();
                        navigate('/login');
                        setTimeout(() => {
                            isLoggingOutRef.current = false;
                        }, 3000);
                    }
                    return Promise.reject(error);
                }

                if (status === 401 && !error.config._retry) {
                    error.config._retry = true;

                    try {
                        const token = await getRefreshToken();

                        // ✅ Attach new token
                        error.config.headers["Authorization"] = `Bearer ${token}`;

                        return axios(error.config);

                    } catch (err) {
                        if (!isLoggingOutRef.current) {
                            doLogout();
                        }
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    // axios.interceptors.response.use(
    //     (response) => response,
    //     (error) => {
    //         if (error.config?.skipLogoutInterceptor) return Promise.reject(error);

    //         if ((error.response && error.response.status === 423) || (error.response && error.response.status === 401)) {
    //             setTimeout(() => {
    //                 doLogout();
    //             }, 2000);
    //         }
    //         return Promise.reject(error);
    //     }
    // );



    const updateSignupData = (field, value) => {
        setSignupData(prev => {
            let newData;
            if (typeof field === 'object' && field !== null) {
                // If the first argument is an object, merge it all at once
                newData = { ...prev, ...field };
            } else {
                // Otherwise, treat as single field/value
                newData = { ...prev, [field]: value };
            }

            if (email) newData.email = email;
            storage.set('signupData', newData);
            return newData;
        });
    };

    // Remove the old useEffect hydration as it's now handled in useState
    // (Line 947-955 was the old useEffect)


    const createAccountScreenOne = () => {
        const firstName = signupData.firstName?.trim();
        const lastName = signupData.lastName?.trim();
        const country = signupData.country?.trim();
        const time_zone = signupData.time_zone?.trim();

        if (!firstName || !lastName || !country || !time_zone) {
            window.showToast('error', screenLabels?.please_enter_all_required_fields?.label_value || 'Please enter all required fields!');
            return;
        }

        // if (signupData.accessCode?.trim() && signupData.accessCode.trim().length > 7) {
        //     window.showToast('error', screenLabels?.access_code_cannot_exceed_7_characters?.label_value || 'Access code cannot exceed 7 characters');
        //     return;
        // }
        storage.set('signupStep', 1);
        navigate('/signup/physical-profile');
    };

    const verifyAccessCode = useCallback(async (accessCode) => {
        if (!deviceId) return;
        const userEmail = storage.get('verifiedEmail');
        const payload = {
            deviceID: deviceId,
            accessCode,
            email: signupData?.email || userEmail,
        };

        try {
            const response = await axios.post(urls.accessCodeVerification, payload);
            const res = response.data;

            if (res.status === true) {
                return { status: true };
            } else {
                return { status: false };
            }
        } catch (error) {
            return { status: false };
        }
    }, [deviceId]);

    const createAccountScreenTwo = (providedData) => {
        const dataToValidate = providedData || signupData;
        const { age, weight, height, race, gender, smoking, waistCircumference, diabetes } = dataToValidate;

        if (!age || !weight || !height || !race || !gender || !smoking || !waistCircumference || !diabetes) {
            window.showToast('error', screenLabels?.please_enter_all_required_fields?.label_value || "Please enter all required fields!");
            return;
        }

        if (diabetes === "yes") {
            window.showToast('error', screenLabels?.please_select_your_diabetes_type?.label_value || "Please select your diabetes type!");
            return;
        }

        const weightVal = parseFloat(weight);
        const heightVal = parseFloat(height);

        if (isNaN(weightVal) || weightVal <= 0) {
            window.showToast('error', screenLabels?.weight_must_be_a_number_greater_than_0?.label_value || "Weight must be a number greater than 0.");
            return;
        }

        if (isNaN(heightVal) || heightVal <= 0) {
            window.showToast('error', screenLabels?.height_must_be_a_number_greater_than_0?.label_value || "Height must be a number greater than 0.");
            return;
        }

        const ageVal = parseInt(age);
        const isUnder18 = ageVal < 18;

        if (isUnder18) {
            window.showToast('error', screenLabels?.you_must_be_at_least_18_years_old_to_continue?.label_value || "You must be at least 18 years old to continue.");
            return;
        }

        storage.set('signupStep', 2);
        if (is4Life) {
            navigate('/signup/lifestyle');
        } else {
            navigate('/signup/wellness-interests');
        }
    };

    const createAccountScreenThree = () => {
        const { activity, vegetables, grains, sugaryFoods, redMeat, fish } = signupData;

        if (!activity || !vegetables || !grains || !sugaryFoods || !redMeat || !fish) {
            window.showToast('error', screenLabels?.please_enter_all_required_fields?.label_value || "Please enter all required fields!");
            return;
        }

        storage.set('signupStep', 3);
        navigate('/signup/wellness-priorities');
    };

    const createAccountScreenFour = () => {
        const { wellnessCategory } = signupData;

        if (!Array.isArray(wellnessCategory) || wellnessCategory.length === 0) {
            window.showToast('error', screenLabels?.please_select_your_wellness_category?.label_value || "Please select your wellness category!");
            return;
        }
        storage.set('signupStep', 4);
        if (is4Life) {
            completeSignup();
        } else {
            navigate('/reset-password');
            storage.set('UserModule', 'setPassword');
        }
    }

    const completeSignup = async () => {
        if (!deviceId) return;
        const requiredFields = [
            'firstName', 'lastName', 'email', 'age', 'weight',
            'height', 'race', 'gender', 'waistCircumference',
            'diabetes', 'wellnessCategory', 'smoking', 'password', 'confirmPassword',
            'vegetables', 'grains', 'sugaryFoods', 'redMeat', 'fish', 'activity', 'time_zone', 'country',
            // 'phoneNumber', 'accessCode'
        ];

        const isFromQR = !!storage.get('refKey');
        const missingFields = requiredFields.filter(field => {
            // Bypass password for 4Life
            if (is4Life && (field === 'password' || field === 'confirmPassword')) return false;
            // Bypass dietary questions for non-4Life (they skip Step 3 screen)
            if (!is4Life && (field === 'vegetables' || field === 'grains' || field === 'sugaryFoods' || field === 'redMeat' || field === 'fish' || field === 'activity')) return false;
            // Bypass phone number ONLY for 4Life (hidden in UI for them)
            if (is4Life && field === 'phoneNumber') return false;
            // Bypass access code for 4Life OR QR scan users (hidden in UI)
            if ((is4Life || isFromQR) && field === 'accessCode') return false;

            const value = signupData[field];
            if (Array.isArray(value)) return value.length === 0;
            return !value;
        });

        if (missingFields.length > 0) {
            console.warn('[completeSignup] Missing fields:', missingFields, 'signupData:', signupData);
            window.showToast('error', `${is4Life ? (screenLabels?.please_complete_all_required_fields?.label_value || 'Please complete all required fields') : (screenLabels?.please_complete_all_previous_steps_before_setting_password?.label_value || 'Please complete all previous steps before setting password')}: ${missingFields.join(', ')}`);
            return;
        }

        if (!is4Life) {
            if (signupData.password !== signupData.confirmPassword) {
                window.showToast('error', screenLabels?.passwords_do_not_match?.label_value || 'Password and confirm password do not match');
                return;
            }

            if (!signupData.password || !signupData.confirmPassword) {
                window.showToast('error', screenLabels?.please_enter_password_and_confirm_password?.label_value || 'Please enter password and confirm password');
                return;
            }
        }


        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            licence_type: licenseType,
            firstName: signupData.firstName?.trim(),
            lastName: signupData.lastName?.trim(),
            email: signupData.email?.trim().toLowerCase() || email?.trim().toLowerCase(),
            secondaryEmail: signupData?.secondaryEmail?.trim().toLowerCase() || verifiedsecEmail?.trim().toLowerCase(),
            mobileNumber: signupData.phoneNumber || '',
            accessCode: signupData.accessCode || '',
            age: signupData.age,
            weight: signupData.weight,
            weightUnit: signupData.weightUnit,
            height: signupData.height,
            heightUnit: signupData.heightUnit,
            race: signupData.race,
            gender: signupData.gender,

            waistCircumference: signupData.waistCircumference,
            waistUnit: signupData.waistUnit,
            diabetes: signupData.diabetes,
            smoking: signupData.smoking,
            hypertension: signupData.hypertension,
            wellnessCategory: signupData.wellnessCategory || '',

            fruits: signupData.fruits,
            vegetables: signupData.vegetables,
            grains: signupData.grains,
            sugaryFoods: signupData.sugaryFoods,
            redMeat: signupData.redMeat,
            fish: signupData.fish,
            activity: signupData.activity,

            password: signupData.password,
            confirm_password: signupData.confirmPassword,
            token: oauthSuccessTokenRef.current || '',
            time_zone: signupData.time_zone,
            country: signupData.country || '',
            role: loginObject?.role || 3,
            ref_code: storage.get('refKey') || '',
            fourlife_token: storage.get('4SSOToken') || '',
            device_data: getDeviceData(),
        };

        console.log(payload);
        // return

        const signupUrl = storage.get('isBack') ? urls.userRejoin : urls.completeSignup;
        await handleApiCall(
            () => axios.post(signupUrl, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    startTokenRefreshTimer();
                    window.showToast('success', res.msg || screenLabels?.account_created_successfully?.label_value || 'Account created successfully!');
                    const decodeAccessToken = jwtDecode(res?.token?.access_token);

                    storage.set('accessToken', res?.token?.access_token);
                    storage.set('refreshToken', res?.token?.refresh_token);
                    storage.set('tokenExpiry', decodeAccessToken?.exp);
                    storage.set('loginObj', decodeAccessToken);
                    storage.set('isLoggedIn', true);
                    storage.remove('isBack');
                    navigate(is4Life ? '/dashboard2' : '/dashboard');

                    setSignupData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        secondaryEmail: '',
                        phoneNumber: '',
                        accessCode: '',
                        age: '',
                        weight: '',
                        weightUnit: 'lbs',
                        heightUnit: 'feet',
                        waistUnit: 'cm',
                        height: '',
                        race: '',
                        gender: '',
                        waistCircumference: '',
                        diabetes: '',
                        smoking: '',
                        hypertension: '',
                        wellnessCategory: [],
                        fruits: '',
                        vegetables: '',
                        grains: '',
                        sugaryFoods: '',
                        redMeat: '',
                        fish: '',
                        activity: '',
                        country: '',
                        time_zone: '',
                        password: '',
                        confirmPassword: ''
                    });
                    storage.set('UserModule', 'Login');
                    storage.remove('signupData');
                    storage.remove('signupStep');
                    storage.remove('refKey');
                } else {

                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            false
        );
    };

    const translateData = useCallback(async (lang, options = {}) => {
        if (!lang || !deviceId) return;

        // remoteVersion: when provided, stamp it into localStorage `version`
        //   after labels are successfully synced so the next load can compare.
        // silentError: suppress the error toast (used by the version-driven
        //   refresh on app open — a failed refresh keeps the cached labels).
        const { remoteVersion = null, silentError = false } = options;

        const payload = {
            deviceID: deviceId,
            language_code: lang,
            companyKey: companyId,
            role: loginObject?.role || 3
        };

        await handleApiCall(
            () => axios.post(urls.traslateLabels, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    let labels = res?.translations?.translations_labels?.data;
                    storage.set("appLabels", labels);
                    setTranslatedLabels(labels);
                    if (remoteVersion !== null) {
                        storage.set("version", remoteVersion);
                    }
                } else if (!silentError) {
                    window.showToast('error', res.msg || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                if (!silentError) {
                    window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            false
        );
    }, [deviceId]);

    // Fetch the voice audio files for the given (or currently selected)
    // language and forward their URLs to the React Native shell so it can
    // play them natively.
    const getVoiceAudioFiles = useCallback(async (lang) => {
        if (!deviceId) return;

        const language = lang || storage.get('selectedLanguage') || 'en';

        // Reuse the shared, per-language voice manifest cache (populated on
        // language change and by the web invite audio) so the endpoint is hit at
        // most once per language — not again on every Start Scan / Guest Scan
        // mount. On a cache hit this just re-posts the files to React Native
        // without a network call.
        const audioFiles = await getVoiceFiles(language);
        if (!audioFiles) return;

        storage.set('voiceAudioFiles', audioFiles);
        // Hand the audio file URLs to React Native.
        window.ReactNativeWebView?.postMessage(
            `voiceAudioFiles|${JSON.stringify(audioFiles)}`
        );
    }, [deviceId]);

    // Load (and re-load on language change) the voice audio files once the
    // device id is available, then push them to React Native.
    // The result is ONLY consumed by the native shell (postMessage), so on the
    // web there's nothing to do — skip it entirely to keep it off the critical
    // path of the login/language screens. Inside the RN WebView we still fetch,
    // but defer it to idle time so it never competes with startup API calls.
    // NOTE: the voice audio files are prefetched on the Start Scan / Guest Scan
    // prep screens (they call getVoiceAudioFiles on mount), not here. That warms
    // the native audio ahead of the scan without fetching for users who never
    // scan, and keeps it off the login/language screens.

    useEffect(() => {
        const deviceID = storage.get('DeviceId');
        if (!deviceID) {
            getDeviceId();
        }
    }, [getDeviceId]);



    useEffect(() => {
        const seen = storage.get("notificationsSeen");
        if (seen === "true") {
            setIsNotificationReceived(false);
        }
    }, []);

    useEffect(() => {
        if (!userId) return;

        const update = async () => {
            if (!deviceId) return;

            try {
                const token = storage.get("accessToken");
                if (!token) return;
                const payload = {
                    deviceID: deviceId,
                    userKey: userId,
                };

                const response = await axios.post(urls.userLastSeen, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const res = response.data;
                if (res.status !== true) {
                    window.showToast("error", res.msg || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                } if (res.status === true && res.newNotification) {
                    // only set true when backend reports NEW notifications
                    setIsNotificationReceived(true);
                    storage.set("notificationsSeen", "false");
                }
            } catch (error) {
                window.showToast(
                    "error",
                    error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                );
            }
        };

        update();
        const interval = setInterval(update, 2 * 60 * 1000);
        return () => clearInterval(interval);
    }, [userId, deviceId]);

    const handleNotificationClick = () => {
        setIsNotificationReceived(false);
        localStorage.setItem("notificationsSeen", "true");
    };

    // One-time hydration: set the language and seed labels from cache so the UI
    // renders immediately without waiting on the version check below.
    useEffect(() => {
        // Public campaign scan routes don't need label translations.
        if (window.location.pathname.startsWith('/campaign/')) return;

        const storedLang = storage.get("selectedLanguage");
        const storedLabels = storage.get("appLabels");
        setSelected(storedLang || 'en');
        if (storedLabels) {
            setTranslatedLabels(storedLabels);
        }
    }, []);

    // Label-version check — runs on mount AND on every route change (no reload
    // needed). If the remote version differs from the cached one (or nothing is
    // cached yet), refetch labels and stamp the new version. A fresh cache is a
    // no-op, so navigating between pages costs only one lightweight version GET.
    useEffect(() => {
        // Seed a baseline version for users who don't have one yet (e.g.
        // accounts created before this feature shipped). This runs on every
        // route change, so the `version` key is present on all pages. Future
        // admin version bumps then differ from this baseline and trigger a
        // label refetch.
        const currentVersion = storage.get("version");
        if (currentVersion === null || currentVersion === undefined) {
            storage.set("version", 100);
        }

        if (window.location.pathname.startsWith('/campaign/')) return;

        const checkLabelVersion = async () => {
            // Skip if a check is already running (rapid navigation).
            if (labelVersionCheckRef.current) return;
            labelVersionCheckRef.current = true;

            try {
                // Read the remote label version stamp. Endpoint failures leave
                // remoteVersion null → we keep the cached labels.
                let remoteVersion = null;
                try {
                    const versionRes = await axios.get(urls.labelVersion(getPlatform()), {
                        withCredentials: false,
                        skipLogoutInterceptor: true,
                    });
                    remoteVersion = versionRes?.data?.version ?? null;

                    if (versionRes?.data?.is_update_version === true) {
                        navigate('/update-app');
                    }
                } catch {
                    // Version endpoint unreachable — fall back to cached labels.
                }

                const storedVersion = storage.get("version");
                const storedLabels = storage.get("appLabels");
                const isStale =
                    remoteVersion !== null &&
                    String(storedVersion) !== String(remoteVersion);

                // Cache is fresh (and present) → nothing to do.
                if (storedLabels && !isStale) return;

                if (isStale) {
                    console.log("New label version found:", { storedVersion, remoteVersion });
                }

                // Refetch labels; stamp the new version, stay silent on failure
                // (keeps cached labels, retries on the next navigation).
                const lang = storage.get("selectedLanguage") || 'en';
                await translateData(lang, { remoteVersion, silentError: true });
            } finally {
                labelVersionCheckRef.current = false;
            }
        };

        checkLabelVersion();
    }, [translateData, location.pathname]);

    useEffect(() => {
        const storageLabels = storage.get("appLabels")
        if (isManualChange && selected) {
            translateData(selected);
            // Re-post the new language's voice audio files to the React Native
            // shell so it plays audio in the language just picked (e.g. from the
            // profile language screen), not the previous one.
            getVoiceAudioFiles(selected);
        }
        else {
            setTranslatedLabels(storageLabels)
        }
    }, [selected, isManualChange, translateData, getVoiceAudioFiles]);

    // Warm the web voice manifest once per language after login so screens that
    // play browser audio (the campaign invite clip) resolve instantly from cache.
    // prefetchVoiceManifest dedupes via its per-language cache, so this hits the
    // API only when the language actually changes — not on every render/mount.
    useEffect(() => {
        if (!userId || !selected) return;
        prefetchVoiceManifest(selected);
    }, [userId, selected]);

    const isEmpty = (value) => {
        return (
            value === null ||
            value === undefined ||
            value === "" ||
            (typeof value === "string" && value.trim() === "") ||
            value === 0
        );
    };

    const getUserDetails = useCallback(async () => {

        if (!deviceId || !userId) return;

        const payload = {
            userKey: userId,
            deviceID: deviceId,
            role: loginObject?.role || 3
        };

        const cacheKey = `cachedUserDetails_${userId}`;
        const cached = storage.get(cacheKey);
        if (cached?.details) {
            setUserDetails(cached.details);
            setServings(cached.servings);
            setNotificationsEnabled(cached.details?.is_fcm_on);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls?.userDetails, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            const details = res?.details;
            const userInfo = {
                userKey: details?.user_key,
                companyKey: details?.company_key,
                firstName: details?.first_name,
                lastName: details?.last_name,
                userEmail: details?.email,
                userSecondaryEmail: details?.secondary_email,
                mobileNumber: details?.mobile_number || null,
                profilePhoto: details?.profile_photo || null,
                role: loginObject?.role || 3,
                deviceID: deviceId || "",
                time_zone: details?.time_zone
            };
            storage.set('loginObj', userInfo);

            if (res?.status === true) {
                const requiredFields = [
                    "gender",
                    "dob",
                    "height",
                    "weight",
                    "waist_circumference",
                    "smoking",
                    "wellness_category",
                    "diabetes",
                    "race",
                ];

                const isProfileIncomplete = requiredFields.some(
                    field => isEmpty(details[field])
                );

                // if (isProfileIncomplete) {
                //     window.showToast("error", "Please complete all required profile fields before starting the scan.");
                //     navigate('/profile-update');
                // }

                setUserDetails(details);
                setServings(res?.servings);
                setNotificationsEnabled(details?.is_fcm_on);
                storage.set(cacheKey, { details, servings: res?.servings });
            } else if (!cached) {
                window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, userId]);

    const getTimeZone = useCallback(async (search = "") => {
        if (!deviceId) return;

        const url = `${urls?.getTimeZone}?search=${search}`;
        const cacheKey = `cachedTimeZone_${search || 'all'}`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setTimeZone(cached);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.get(url, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                setTimeZone(res?.list);
                storage.set(cacheKey, res?.list);
            } else if (!cached) {
                window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId]);


    const updateUserDetails = async (dataToUpdate) => {
        if (!deviceId || !userId) return;
        const { height, weight, waist_circumference } = dataToUpdate
        const heightVal = parseFloat(height);
        const weightVal = parseFloat(weight);
        const waistVal = parseFloat(waist_circumference);

        if (isNaN(heightVal) || heightVal <= 0) {
            window.showToast('error', screenLabels?.height_must_be_a_number_greater_than_0?.label_value || 'Height must be a number greater than 0.');
            return;
        }

        if (isNaN(weightVal) || weightVal <= 0) {
            window.showToast('error', screenLabels?.weight_must_be_a_number_greater_than_0?.label_value || 'Weight must be a number greater than 0.');
            return;
        }

        if (isNaN(waistVal) || waistVal <= 0) {
            window.showToast('error', screenLabels?.waist_circumference_must_be_a_number_greater_than_0?.label_value || 'Waist circumference must be a number greater than 0.');
            return;
        }

        // if (
        //     mobileNumber &&
        //     !isValidPhoneNumber("+" + mobileNumber)
        // ) {
        //     window.showToast("error", "Please enter a valid phone number");
        //     return;
        // }

        // const dobDate = new Date(dob);
        const today = new Date();
        // const age = today.getFullYear() - dobDate.getFullYear();
        // const monthDiff = today.getMonth() - dobDate.getMonth();
        // const dayDiff = today.getDate() - dobDate.getDate();
        // const isUnder18 = age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)));

        // if (isUnder18) {
        //     window.showToast('error', 'User must be at least 18 years old.');
        //     return;
        // }

        const payload = {
            userID: userId,
            deviceID: deviceId,
            role: loginObject?.role || 3,
            ...dataToUpdate
        };

        return await handleApiCall(
            (config) => axios.post(urls?.updateUserDetails, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast('success', res.msg || screenLabels?.user_details_updated?.label_value || 'User details updated');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
                return res;
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    };

    const deleteUserAcc = async () => {

        // if (!deviceId || !email) return;
        if (!deviceId || (is4Life ? !verifiedsecEmail : !email)) return;

        const payload = {
            // email: email,
            email: is4Life ? verifiedsecEmail : email,
            deviceID: deviceId,
            companyKey: companyId,
        };

        return await handleApiCall(
            (config) => axios.post(urls?.deleteUserAcc, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast('success', res.msg || screenLabels?.user_deleted?.label_value || 'User Deleted');
                    if (storage.get('isLoggedIn') == true) {
                        // navigate('/login');
                        storage.clear();
                        window?.ReactNativeWebView?.postMessage("googlesignOut");
                        getDeviceId();
                        getCompanyBranding();
                        navigate('/login');
                    } else {
                        setIsThankYou(true);
                    }

                    // storage.remove('loginObj');
                    // storage.remove('verifiedEmail');
                    // storage.remove('UserModule');
                    // storage.remove('verifiedSecondaryEmail');

                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
                return res;
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }


    const getFaqList = useCallback(async () => {
        if (!companyId || !deviceId || !userId) return;
        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            faq_id: '',
            adminKey: '',
            role: loginObject?.role || 3,
            userKey: userId,
            languageCode: selected,
        };

        await handleApiCall(
            (config) => axios.post(urls.getFaqList, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    setFaqList(res?.faq)
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [companyId, deviceId, userId, selected]);

    const uploadCropImg = async (blob) => {
        if (!userId) return;
        const formData = new FormData();
        formData.append("userID", userId);
        formData.append("image", blob, "profile.jpg");
        formData.append("slug", "user_profile");
        formData.append("role", loginObject?.role || 3);

        await handleApiCall(
            (config) =>
                axios.post(urls.uploadCroppedImg, formData, {
                    ...config,
                    headers: { "Content-Type": "multipart/form-data" },
                }),
            async (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast("success", screenLabels?.profile_image_updated?.label_value || "Profile image updated!");
                    await getUserDetails();
                } else {
                    window.showToast("error", res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                console.error(error);
                window.showToast(
                    "error",
                    error?.response?.data?.error ||
                    error.message ||
                    screenLabels?.something_went_wrong?.label_value ||
                    "Something went wrong"
                );
            },
            true
        );
    };



    const getLanguageList = useCallback(async () => {
        if (!deviceId) return;
        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
        };

        const cacheKey = `cachedLanguageList`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setLanguages(cached);
        }

        try {
            const response = await axios.post(urls?.getLanguage, payload);
            const res = response?.data;
            if (res?.status === true) {
                setLanguages(res?.list);
                storage.set(cacheKey, res?.list);
            } else if (!cached) {
                window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            }
            return res;
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId])

    useEffect(() => {
        getCompanyBranding();
    }, [getCompanyBranding]);

    const whiteLabel = useWhiteLabel();

    useEffect(() => {
        if (whiteLabel) {
            const root = document.documentElement;
            root.style.setProperty('--primaryColor', whiteLabel.primary_color || '');
            root.style.setProperty('--secondaryColor', whiteLabel.secondary_color || '');
            root.style.setProperty('--textColor', whiteLabel.text_color || '');
            root.style.setProperty('--headingColor', whiteLabel.heading_color || '');
            root.style.setProperty('--buttonColor', whiteLabel.button_color || '');
            root.style.setProperty('--buttonHoverColor', whiteLabel.button_color_hover || '');
            root.style.setProperty('--buttonTxtColor', whiteLabel.button_text_color || '');
            root.style.setProperty('--buttonTxtHoverColor', whiteLabel.button_text_color_hover || '');
            root.style.setProperty('--darkcolor', whiteLabel.text_color || '');

            root.style.setProperty('--headerBgColor', whiteLabel.header_background_color || '');
            root.style.setProperty('--headerTextColor', whiteLabel.header_text_color || '');
            root.style.setProperty('--footerBgColor', whiteLabel.footer_background_color || '');
            root.style.setProperty('--footerTextColor', whiteLabel.footer_text_color || '');
            root.style.setProperty('--footerIconColor', whiteLabel.footer_icon_color || '');
            root.style.setProperty('--footerMenuActiveColor', whiteLabel.footer_menu_active_color || '');
            root.style.setProperty('--sidebarBgColor', whiteLabel.sidebar_color || '');
            root.style.setProperty('--sidebarTextColor', whiteLabel.sidebar_text_color || '');
            root.style.setProperty('--sidebarIconColor', whiteLabel.sidebar_icon_color || '');
            root.style.setProperty('--sidebarActiveMenuColor', whiteLabel.sidebar_menu_active_color || '');
            root.style.setProperty('--pageBgColor', whiteLabel.page_background_color || '');
            root.style.setProperty('--linkColor', whiteLabel.link_color || '');

            const baseUrl = imagePath;
            const companyLogoUrl = whiteLabel?.company_logo ? baseUrl + whiteLabel.company_logo : '';
            const appHeaderLogoUrl = whiteLabel?.app_header_logo ? baseUrl + whiteLabel.app_header_logo : '';
            setLogoUrl(companyLogoUrl);
            setHeaderLogoUrl(appHeaderLogoUrl || companyLogoUrl);
        }
    }, [whiteLabel]);

    const uploadDocument = async () => {
        if (!deviceId || !userId || !companyId) return;
        const { file, selectedCategory, documentID } = documentData;

        if (!file || !selectedCategory || selectedCategory === 'all') {
            window.showToast("error", screenLabels?.both_category_and_file_are_required?.label_value || "Both category and file are required.");
            return;
        }

        const formData = new FormData();
        formData.append("companyID", companyId);
        formData.append("deviceID", deviceId);
        formData.append("userId", userId);
        formData.append("category", selectedCategory);
        formData.append("document", file);

        if (documentID) {
            formData.append("documentID", documentID);
        }

        await handleApiCall(
            (config) => axios.post(urls.uploadDocument, formData, {
                ...config,
                headers: {
                    ...config.headers,
                    'Content-Type': 'multipart/form-data',
                }
            }),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast("success", res.msg || (documentID ? (screenLabels?.updated_successfully?.label_value || "Updated successfully") : (screenLabels?.uploaded_successfully?.label_value || "Uploaded successfully")));
                    setDocumentData({
                        file: null,
                        previewUrl: "",
                        selectedCategory: "",
                        documentID: null,
                    });
                    storage.remove("documentUpdate")
                    navigate('/documents');
                } else {
                    window.showToast("error", res?.error || (documentID ? (screenLabels?.update_failed?.label_value || "Update failed") : (screenLabels?.upload_failed?.label_value || "Upload failed")));
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    };

    const getDocumentList = useCallback(async () => {
        if (!deviceId || !userId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            role: loginObject?.role || 3,
            adminKey: '',
            category: '',
            companyKey: companyId
        };

        await handleApiCall(
            (config) => axios.post(urls.getDocumentList, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    const formatted = res?.documents?.map((item) => {
                        const [date, time] = item.created_at.split(" ");
                        const [y, m, d] = date.split("-");
                        const isPM = +time.split(":")[0] >= 12;
                        const hour = (+time.split(":")[0] % 12) || 12;
                        const formattedTime = `${hour}:${time.split(":")[1]} ${isPM ? "PM" : "AM"}`;

                        return {
                            id: item.id,
                            key: item.category,
                            fileName: item.file_name,
                            date: `${d}/${m}/${y}`,
                            time: formattedTime,
                            fullPath: item.file_name,
                            userName: item.user_name,
                            status: item.status,
                            file_size: item.file_size
                        };
                    });

                    setUploadedFiles(formatted);
                } else {
                    window.showToast("error", res?.error || screenLabels?.upload_failed?.label_value || "Upload failed");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId, companyId]);

    const deletedocument = useCallback(async (documentId) => {

        if (!deviceId || !documentId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            documentID: documentId,
            role: loginObject?.role || 3
        };

        await handleApiCall(
            (config) => axios.post(urls.deleteDocument, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', res.msg);
                    setUploadedFiles(prevFiles => prevFiles.filter(doc => doc.id !== documentId));
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast(
                    'error',
                    error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            },
            true
        );
    }, [deviceId, userId]);

    const getTermsConditions = useCallback(async () => {
        if (!deviceId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            role: loginObject?.role || 3,
            companyKey: companyId,
            adminKey: '',
            languageCode: selected,
        };

        const cacheKey = `cachedTermsConditions`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setDocumentContent(prev => ({ ...prev, terms: cached }));
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.getTermsConditions, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                const terms = res?.details?.terms_conditions || '';
                setDocumentContent(prev => ({ ...prev, terms }));
                storage.set(cacheKey, terms);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, companyId, selected]);


    const getPrivacypolicy = useCallback(async () => {
        if (!deviceId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            role: loginObject?.role || 3,
            companyKey: companyId,
            adminKey: '',
            languageCode: selected,
        };

        const cacheKey = `cachedPrivacyPolicy`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setDocumentContent(prev => ({ ...prev, privacy: cached }));
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.getPrivacypolicy, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                const privacy = res?.details?.privacy_policy || '';
                setDocumentContent(prev => ({ ...prev, privacy }));
                storage.set(cacheKey, privacy);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, companyId, selected]);

    const getArticlesList = useCallback(async (page = 1, perPage = 10) => {
        if (!deviceId || !companyId || !userId || !selected) return;

        if (prevLangRef.current !== selected || prevSearchRef.current !== search) {
            page = 1;
            prevLangRef.current = selected;
            prevSearchRef.current = search;
        }

        const payload = {
            deviceID: deviceId,
            role: loginObject?.role || 3,
            companyKey: companyId,
            userKey: userId,
            languageCode: selected,
            page: page,
            search: search
        };

        try {
            const freshToken = storage.get("accessToken");
            const config = {
                headers: { Authorization: `Bearer ${freshToken}` }
            };

            const response = await axios.post(urls.getArticlesList, payload, config);
            const res = response.data;

            if (res.status === true) {
                const articleData = res?.article;
                setArticlesList(articleData?.data || []);
                setPagination({
                    totalRecords: articleData?.total_records || 0,
                    totalPages: articleData?.total_pages || 1,
                    currentPage: page,
                    perPage: articleData?.per_page || perPage
                });
            } else {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (error) {
            window.showToast(
                "error",
                error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
            );
        }
    }, [deviceId, companyId, userId, selected, search]);


    const resetArticlesSearch = useCallback(() => {
        setSearch("");
        prevSearchRef.current = "";
        prevLangRef.current = selected;
    }, [selected]);

    const getArticleDetailsByID = useCallback(
        async (article_id) => {
            if (!deviceId || !companyId || !userId || !article_id) return;

            const payload = {
                deviceID: deviceId,
                role: loginObject?.role || 3,
                companyKey: companyId,
                userKey: userId,
                article_id: article_id,
            };

            setArticleDetails({});

            await handleApiCall(
                (config) => axios.post(urls.getArticldetailsById, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        setArticleDetails(res.article);
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );

    const normalizePercent = (val) => {
        if (val === null || val === undefined || isNaN(val)) return null;
        return Math.min(100, Math.max(0, Number(val)));
    };

    // Several factors come back without the `percent` triple the range bar needs
    // (immune/metabolic/sugar factors typically ship only min/max/val). Place the
    // value on a padded min..max scale so the biomarker still renders instead of
    // being dropped — which would otherwise hide the whole category.
    const derivePercents = (rawVal, rawMin, rawMax) => {
        if (rawVal === "" || rawVal === null || rawVal === undefined) return null;

        const value = Number(rawVal);
        const min = Number(rawMin);
        const max = Number(rawMax);
        if (isNaN(value) || isNaN(min) || isNaN(max) || max <= min) return null;

        const pad = (max - min) * 0.2;
        const scaleMin = min - pad;
        const scaleMax = max + pad;
        const toPercent = (n) =>
            Math.min(100, Math.max(0, ((n - scaleMin) / (scaleMax - scaleMin)) * 100));

        return {
            percent: toPercent(value),
            minPercent: toPercent(min),
            maxPercent: toPercent(max),
        };
    };

    const mapBiomarkers = (factors = {}, screenLabels, parentColor, normalizedScanDetails) => {
        return Object.entries(factors)
            .map(([key, val]) => {
                let percent = normalizePercent(val?.percent);
                let minPercent = normalizePercent(val?.min_percent);
                let maxPercent = normalizePercent(val?.max_percent);
                const avgValue = val?.avg_value ?? null;
                const normalValue = val?.normal_value ?? null;
                const unit = val?.unit || "";
                const value = val?.val ?? null;
                // scan_spectrum_v2 is no longer the only source of range data —
                // biomarker_score factors now carry their own `slider` (min/max/
                // position/gradient), which the dashboard bar renders directly.
                const slider = val?.slider || null;
                const color = val?.color || null;
                // zone.color mirrors what scan_spectrum_v2 used to provide —
                // the marker's exact color for the current position.
                const zone = val?.zone || null;

                if (percent === null) {
                    // Derive the whole triple together so the bar stays self-consistent.
                    // The top-level min/max are frequently "0"/"0" once a slider is
                    // present, so fall back to the slider's own min/max/input_value.
                    const derived =
                        derivePercents(val?.val, val?.min, val?.max) ||
                        (slider &&
                            derivePercents(slider.input_value, slider.min, slider.max));
                    if (!derived) return null;
                    percent = derived.percent;
                    minPercent = derived.minPercent;
                    maxPercent = derived.maxPercent;
                }
                if (minPercent === null || maxPercent === null) return null;

                // The raw factor `value` above is only a numeric code used for
                // the bar-position math — for display, prefer the real
                // translated reading already parsed onto this same key in
                // normalizedScanDetails (see findNormalizedValue), falling
                // back to the raw code when no such entry exists.
                const normalizedMatch = findNormalizedValue(key, normalizedScanDetails);
                const displayValue = normalizedMatch ? normalizedMatch.value : value;
                const displayUnit = normalizedMatch ? normalizedMatch.unit : unit;

                return {
                    name: formatBiomarkerName(key, screenLabels, normalizedScanDetails),
                    rawKey: key,
                    percent,
                    minPercent,
                    maxPercent,
                    normalValue,
                    avgValue,
                    unit: displayUnit,
                    value: displayValue,
                    shadowColor: parentColor,
                    slider,
                    color,
                    zone,
                };
            })
            .filter(Boolean);
    };

    const fetchFreshProducts = useCallback(
        async (scanID = "") => {
            if (!deviceId || !companyId || !userId) return;
            await new Promise((resolve) => setTimeout(resolve, 4000));
            try {
                const token = storage.get('accessToken');
                const payload = {
                    deviceID: deviceId,
                    userKey: userId,
                    scanID: scanID,
                    role: loginObject?.role || 3,
                };
                const response = await axios.post(urls.recProductList, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const res = response.data;
                if (res.status === true) {
                    const list = res?.list || [];
                    const formatted = list.map((item, index) => ({
                        id: item.item_id || index,
                        name: item.short_desc,
                        image: item?.image !== null ? item?.image : images.placeHolderImg,
                        description: item.reasons?.length
                            ? `Helps with ${item.reasons.slice(0, 3).map(r => r.name).join(" & ")}`
                            : "Recommended for you",
                    }));
                    setProducts(prev => ({
                        ...prev,
                        scanId: scanID,
                        top4: formatted.slice(0, 4),
                        remaining: formatted.slice(4),
                    }));
                    setIsProfileUpdated(false);
                }
            } catch {
                setIsProfileUpdated(false);
            }
        },
        [deviceId, companyId, userId]
    );

    const applyScanResponse = useCallback((res) => {
        setIsProfileUpdated(res?.is_profile === true);
        if (res?.is_active == false) {
            storage.set('isPlanActive', false)
        } else {
            storage.set('isPlanActive', true)
        }

        const vendorUserKey = res?.venderDetails?.vender_user_key;
        const vendorProjectKey = res?.venderDetails?.vender_project_key;
        storage.set('chatVendorUserKey', vendorUserKey);
        storage.set('chatVendorProjectKey', vendorProjectKey);
        const scanData = res?.scanList?.scan_data;
        const recommendations = res?.scanList?.recommendation || [];
        const biomarkerScore = res?.scanList?.biomarker_score;
        setTopSection(biomarkerScore?.top_section || null);

        // Reuse normalizeScanDetails' is4Life-aware labels for biomarker card
        // names instead of formatBiomarkerName() re-deriving them from the key.
        const normalizedForLabels = normalizeScanDetails(scanData || {}, screenLabels, is4Life);

        const dynamicTypes = {
            cardiovascular: {
                label: screenLabels?.cardiovascular?.label_value || "Cardiovascular",
                color: biomarkerScore?.cv_color,
                score: Number(biomarkerScore?.cv_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.cv_factors, screenLabels, biomarkerScore?.cv_color, normalizedForLabels),
            },
            immune: {
                label: screenLabels?.immune?.label_value || "Immune",
                color: biomarkerScore?.immune_color,
                score: Number(biomarkerScore?.immune_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.immune_factors, screenLabels, biomarkerScore?.immune_color, normalizedForLabels),
            },
            metabolic: {
                label: screenLabels?.metabolic?.label_value || "Metabolic",
                color: biomarkerScore?.metabolic_color,
                score: Number(biomarkerScore?.metabolic_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.metabolic_factors, screenLabels, biomarkerScore?.metabolic_color, normalizedForLabels),
            },
            stress: {
                label: screenLabels?.stress?.label_value || "Stress",
                color: biomarkerScore?.stress_color,
                score: Number(biomarkerScore?.stress_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.stress_factors, screenLabels, biomarkerScore?.stress_color, normalizedForLabels),
            },
            sugar: {
                label: screenLabels?.sugar?.label_value || "Sugar",
                color: biomarkerScore?.sugar_color,
                score: Number(biomarkerScore?.sugar_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.sugar_factors, screenLabels, biomarkerScore?.sugar_color, normalizedForLabels),
            },
        };
        const cleanedDynamicTypes = Object.fromEntries(
            Object.entries(dynamicTypes).filter(
                ([_, type]) => type.biomarkers.length > 0
            )
        );

        console.log(cleanedDynamicTypes, "cleanedDynamicTypes")

        setDynamicTypes(cleanedDynamicTypes);

        setScanDetails(scanData);
        // The spectrum maps sit on the scan wrapper for /scan-details, but the
        // campaign endpoint returns them at the response root alongside
        // `scanDetail` — accept either so the invite result renders the v2
        // colour bands instead of falling back to the legacy gradient.
        const spectrum =
            res?.scanList?.scan_spectrum || res?.scan_spectrum || scanData?.scan_spectrum || {};
        const spectrumV2 =
            res?.scanList?.scan_spectrum_v2 || res?.scan_spectrum_v2 || scanData?.scan_spectrum_v2 || {};
        setScanSpectrum(spectrum);
        setScanSpectrumV2(spectrumV2);
        if (Object.keys(spectrumV2).length === 0) {
            console.warn('[scan] no scan_spectrum_v2 in response — falling back to legacy spectrum', res);
        }
        setTempScanDetails(scanData);
        setRedirection({
            productLink: res?.scanList?.product_link,
            productShare: res?.scanList?.product_share
        });
        const formattedProducts = recommendations.map((item, index) => ({
            id: item.item_id || index,
            name: item.short_desc,
            image: item?.image !== null ? item?.image : images.placeHolderImg,
            description: item.reasons?.length
                ? `Helps with ${item.reasons.slice(0, 3).map(r => r.name).join(" & ")}`
                : "Recommended for you",
        }));

        setProducts({
            scanId: res.scanList?.scanID,
            top4: formattedProducts.slice(0, 4),
            remaining: formattedProducts.slice(4),
            shareLink: "https://example.com/recommendations/123",
            date: res?.scanList?.converted_time,
        });
    }, [screenLabels]);

    const getScanDetails = useCallback(
        async (scanID = "") => {
            let deviceId = storage.get("DeviceId");

            if (!deviceId || !userId || !companyId) return;

            const cacheKey = `cachedScanResponse_${userId}_${scanID || 'latest'}`;
            const cached = storage.get(cacheKey);

            if (cached) {
                applyScanResponse(cached);
            }

            const payload = {
                deviceID: deviceId,
                role: loginObject?.role || 3,
                userKey: userId,
                scanID: scanID,
                adminKey: "",
                licence_type: licenseType,
                companyKey: companyId
            };

            try {
                const freshToken = storage.get('accessToken');
                const response = await axios.post(urls.getScanDetails, payload, {
                    headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
                });
                const res = response?.data;
                if (res?.status === true) {
                    applyScanResponse(res);
                    storage.set(cacheKey, res);
                } else if (!cached) {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            } catch (err) {
                if (!cached) {
                    window.showToast(
                        "error",
                        err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                }
            }
        },
        [deviceId, companyId, userId, applyScanResponse]
    );

    const getRecProductList = useCallback(
        async (scanID = "") => {
            if (!deviceId || !companyId || !userId) return;

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                scanID: scanID,
                role: loginObject?.role || 3,
            };

            await handleApiCall(
                (config) => axios.post(urls.recProductList, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        setFullProducts({
                            scanId: scanID,
                            fullList: res?.list || [],
                        });
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );
    const getScanHistory = useCallback(
        async (date) => {
            if (!deviceId || !companyId || !userId) return;

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                date: date,
            };

            await handleApiCall(
                (config) => axios.post(urls.getScanHistory, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        setScanHistory(res);
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );
    const getWellnessCat = useCallback(async () => {
        if (!deviceId) return;

        const payload = {
            deviceID: deviceId,
        };

        const cacheKey = `cachedWellnessCat`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setWellnessCat(cached);
        }

        try {
            const response = await axios.post(urls.listWellnessCat, payload);
            const res = response?.data;
            if (res?.status === true) {
                const data = res?.translations || res?.list;
                setWellnessCat(data);
                storage.set(cacheKey, data);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId]);

    const listBioMarker = useCallback(async () => {
        if (!deviceId || !selected) return;

        const payload = {
            deviceID: deviceId,
            languageCode: selected,
            companyKey: companyId,
            role: loginObject?.role || 3
        };

        const cacheKey = `cachedBioMarker_${selected}`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setBioMarker(cached);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.listBioMarker, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                const data = res?.list || res?.translations?.translations_labels?.data;
                setBioMarker(data);
                storage.set(cacheKey, data);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    "error",
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                );
            }
        }
    }, [deviceId, selected]);

    const editUserDetails = useCallback(
        async (details) => {
            if (!deviceId || !userId || !companyId) return;

            // let age = "";
            // if (details.dob) {
            //     const today = new Date();
            //     const dob = new Date(details.dob);
            //     age = today.getFullYear() - dob.getFullYear();
            //     const m = today.getMonth() - dob.getMonth();
            //     if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            //         age--;
            //     }
            // }

            if (!details.age || details.age < 18) {
                window.showToast("error", screenLabels?.please_enter_a_valid_age_18_or_above?.label_value || "Please enter a valid age (18 or above).");
                setMsgLoader(false);
                return;
            }
            let translatedLabels = storage.get('appLabels');


            const labels = {
                // Existing keys
                pulse_wait: translatedLabels?.common?.pulse_wait,
                no_detect_face: translatedLabels?.common?.no_detect_face,
                no_detect_face_note: translatedLabels?.common?.no_detect_face_note,
                face_detect: translatedLabels?.common?.face_detect,
                scan_comment: translatedLabels?.common?.scan_comment,
                stop: translatedLabels?.common?.stop_scan,
                adjust_face: translatedLabels?.common?.adjust_face,
                scan_stopped_note: translatedLabels?.common?.scan_stopped_note,
                scan_stopped: translatedLabels?.common?.scan_stopped,

                // Top Bar
                app_title: translatedLabels?.common?.app_title,
                front_camera_capture: translatedLabels?.common?.front_camera_capture,
                back_camera_capture: translatedLabels?.common?.back_camera_capture,

                // Oval Label
                position_face: translatedLabels?.common?.position_face,
                locking: translatedLabels?.common?.locking,
                hold_still: translatedLabels?.common?.hold_still,

                // Bottom Status Card
                readiness: translatedLabels?.common?.readiness,
                searching_for_face: translatedLabels?.common?.searching_for_face,
                stabilizing_frame: translatedLabels?.common?.stabilizing_frame,
                motion_detected: translatedLabels?.common?.motion_detected,
                capturing_signal: translatedLabels?.common?.capturing_signal,
                ready_to_begin: translatedLabels?.common?.ready_to_begin,
                locked: translatedLabels?.common?.locked,
                align: translatedLabels?.common?.align,

                // Progress
                of: translatedLabels?.common?.of,
                ready_to_scan: translatedLabels?.common?.ready_to_scan,

                // Buttons
                start_scan: translatedLabels?.common?.start_scan,
                hold_still_btn: translatedLabels?.common?.hold_still_btn,
                locking_face: translatedLabels?.common?.locking_face,
                look_at_camera: translatedLabels?.common?.look_at_camera,

                // Status Messages
                low_light: translatedLabels?.common?.low_light,
                too_bright: translatedLabels?.common?.too_bright,
                move_back: translatedLabels?.common?.move_back,
                move_closer: translatedLabels?.common?.move_closer,
                center_face: translatedLabels?.common?.center_face,
                look_at_camera_msg: translatedLabels?.common?.look_at_camera_msg,
                hold_still_lock: translatedLabels?.common?.hold_still_lock,
                hold_still_msg: translatedLabels?.common?.hold_still_msg,
                face_locked_start: translatedLabels?.common?.face_locked_start,
                hold_phone_eye_level: translatedLabels?.common?.hold_phone_eye_level,

                // Scan Tips
                tip_face_centered: translatedLabels?.common?.tip_face_centered,
                tip_hold_steady: translatedLabels?.common?.tip_hold_steady,
                tip_even_light: translatedLabels?.common?.tip_even_light,
                blink_to_start: translatedLabels?.common?.blink_to_start,
                blink_to_start_hint: translatedLabels?.common?.blink_to_start_hint,
                scanning_hold: translatedLabels?.common?.scanning_hold,
                open_eyes_msg: translatedLabels?.common?.open_eyes_msg,
                // App scan messages (anti-spoof / face cover)
                verify_face: translatedLabels?.common?.verify_face,
                uncover_face: translatedLabels?.common?.uncover_face,
                blink_not_detected: translatedLabels?.common?.blink_not_detected,
                multiple_faces: translatedLabels?.common?.multiple_faces,

                // Web Scan UI (new labels)
                app_title: translatedLabels?.common?.app_title,
                please_wait: translatedLabels?.common?.please_wait,
                low_fps_alert: translatedLabels?.common?.low_fps_alert,
                seconds_unit: translatedLabels?.common?.seconds_unit,
                scan_progress: translatedLabels?.common?.scan_progress,
                live_signal: translatedLabels?.common?.live_signal,
                rppg_label: translatedLabels?.common?.rppg_label,
                scan_stopped: translatedLabels?.common?.scan_stopped,
                camera_permission_denied: translatedLabels?.common?.camera_permission_denied,
                no_camera_found: translatedLabels?.common?.no_camera_found,
                close_scan: translatedLabels?.common?.close_scan,
                mute_voice_guidance: translatedLabels?.common?.mute_voice_guidance,
                unmute_voice_guidance: translatedLabels?.common?.unmute_voice_guidance,

                // Processing Overlay
                generating_health_report: translatedLabels?.common?.generating_health_report,
                analysing_vitals: translatedLabels?.common?.analysing_vitals,

                // Camera Permission Screen
                camera_scan_title: translatedLabels?.common?.camera_scan_title,
                camera_access_needed: translatedLabels?.common?.camera_access_needed,
                camera_permission_text: translatedLabels?.common?.camera_permission_text,
                allow_camera_access: translatedLabels?.common?.allow_camera_access,
                no_camera_device: translatedLabels?.common?.no_camera_device,

                initializing_audio: translatedLabels?.common?.initializing_audio,

                // Low Quality Modal
                unreliable_signal_title: translatedLabels?.common?.unreliable_signal_title,
                unreliable_signal_body: translatedLabels?.common?.unreliable_signal_body,
                unreliable_signal_footer: translatedLabels?.common?.unreliable_signal_footer,
                // min_quality_required: translatedLabels?.common?.min_quality_required,
                scan_again: translatedLabels?.common?.scan_again,

                // Scan Verification / Status
                face_verification_failed_title: translatedLabels?.common?.face_verification_failed_title,
                face_verification_failed_body: translatedLabels?.common?.face_verification_failed_body,
                face_verification_failed_footer: translatedLabels?.common?.face_verification_failed_footer,
                measurement_inconclusive_title: translatedLabels?.common?.measurement_inconclusive_title,
                measurement_inconclusive_body: translatedLabels?.common?.measurement_inconclusive_body,
                measurement_inconclusive_footer: translatedLabels?.common?.measurement_inconclusive_footer,
                scan_timeout_body: translatedLabels?.common?.scan_timeout_body,
                uncover_face: translatedLabels?.common?.uncover_face,
                verify_face: translatedLabels?.common?.verify_face,
                blink_not_detected: translatedLabels?.common?.blink_not_detected,
                multiple_faces: translatedLabels?.common?.multiple_faces,
            };

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                role: loginObject?.role || 3,
                gender: details.gender,
                diabetes: details.diabetes,
                weightUnit: details.weightUnit,
                weight: details.weight,
                heightUnit: details.heightUnit,
                height: details.height,
                smoking: details.smoking,
                dob: details.dob,
                age: details.age,
                race: details.race,
                cameraMode: details.cameraMode,
                labels: labels,
                language: storage.get('selectedLanguage') || 'en',
                // Tell native whether this is a guest scan so it skips applying
                // the logged-in user's BP calibration baseline (which would be
                // incorrect when scanning someone other than the calibrated user).
                isGuest: !!storage.get("scanGuest"),
            };

            await handleApiCall(
                (config) => axios.post(urls.updateUserData, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        storage.set('loginObj', res?.data);
                        // let isPlanActive = storage.get('isPlanActive');
                        // if (isPlanActive == true) {
                        // console.log("=== StartScan Payload ===");
                        // console.log("Full Payload:", payload);
                        // console.log("Camera Mode:", payload.cameraMode);
                        // console.log("Race:", payload.race);
                        // console.log("Gender:", payload.gender);
                        // console.log("Age:", payload.age);
                        // console.log("=======================");
                        window.ReactNativeWebView?.postMessage(`startUserScan|${JSON.stringify(payload)}`);
                        // } else {
                        //     setMsgLoader(false);
                        //     navigate('/plan-expired');
                        // }
                    } else {
                        // Scan couldn't start — let the guideline modal reappear next
                        // time so the user gets a fresh chance to review it.
                        resetGuidelines('scanGuidelinesSeen');
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                        setMsgLoader(false);
                        navigate('/plan-expired');
                    }
                },
                (error) => {
                    resetGuidelines('scanGuidelinesSeen');
                    setMsgLoader(false);
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error?.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                }
            );
        },
        [deviceId, userId, companyId, isPlanActive]
    );

    useEffect(() => {
        const disableContextMenu = (e) => e.preventDefault();
        const disableCopy = (e) => e.preventDefault();
        const disableSelection = (e) => e.preventDefault();

        document.addEventListener("contextmenu", disableContextMenu);
        document.addEventListener("copy", disableCopy);
        document.addEventListener("selectstart", disableSelection);

        return () => {
            document.removeEventListener("contextmenu", disableContextMenu);
            document.removeEventListener("copy", disableCopy);
            document.removeEventListener("selectstart", disableSelection);
        };
    }, []);


    const exportScanDetails = useCallback(
        async (email, scanID, documentID, module, pdfLanguage) => {
            if (!deviceId || !userId || !email || (module !== "Document" && !scanID)) return;
            const storedLang = storage.get("selectedLanguage");
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                email,
                scanID,
                documentID,
                module,
                languageCode: storedLang,
                pdfLanguage: pdfLanguage || storedLang,
            };

            try {
                const response = await handleApiCall(
                    (config) => axios.post(urls.exportUserScanDetails, payload, config),
                    (response) => response.data
                );

                const res = response?.data || response;
                if (res?.status === true) {
                    window.showToast("success", res.message);
                    navigate(is4Life ? '/dashboard2' : '/dashboard');
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return false;
                }
            } catch (err) {
                window.showToast("error", screenLabels?.export_failed?.label_value || "Export failed");
                return false;
            }
        },
        [deviceId, userId]
    );

    const getHistoryDates = useCallback(async () => {
        if (!deviceId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            role: loginObject?.role || 3
        };
        await handleApiCall(
            (config) => axios.post(urls.getHistoryDates, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setHistoryDates(res?.list || [])
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId]);


    const getNotificationList = useCallback(async () => {
        if (!deviceId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            role: loginObject?.role || 3
        };
        await handleApiCall(
            (config) => axios.post(urls.getNotificationList, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setNotification(res?.list || [])
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId]);

    // Returns the raw response ({status, list, pagination}) instead of pushing
    // into shared context state — the Scan Logs page owns its own list/page
    // state since nothing else in the app needs this data.
    const getScanLogList = useCallback(async ({ scanType = "", scanStatus = "", page = 1 } = {}) => {
        if (!deviceId || !userId) return null;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            scanType,
            scanStatus,
            page,
        };
        return await handleApiCall(
            (config) => axios.post(urls.getScanLogList, payload, config),
            (response) => response.data,
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                return null;
            },
            true
        );
    }, [deviceId, userId]);

    const readNotifications = useCallback(async () => {
        if (!deviceId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            role: loginObject?.role || 3
        };
        await handleApiCall(
            (config) => axios.post(urls.readNotification, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {

                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId]);

    const updateUserLanguage = useCallback(async () => {
        if (!deviceId || !userId || !selected) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            languageCode: selected
        };

        await handleApiCall(
            (config) => axios.post(urls.updateUserLanguage, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId, selected]);

    const notificationOnOff = useCallback(
        async (status) => {
            if (!deviceId || !userId) return;
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                role: loginObject?.role || 3,
                status
            };

            try {
                const response = await handleApiCall(
                    (config) => axios.post(urls.notificationOnOff, payload, config),
                    (response) => response.data
                );
                const res = response?.data || response;
                if (res?.status === true) {
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return false;
                }
            } catch (err) {
                window.showToast("error", screenLabels?.export_failed?.label_value || "Export failed");
                return false;
            }
        },
        [deviceId, userId]
    );

    useEffect(() => {
        window.loginWithGoogle = async (token) => {
            let deviceID = storage.get('DeviceId');
            const payload = { deviceID, token, companyKey: companyId, licence_type: licenseType, device_data: getDeviceData() };

            try {
                const response = await handleApiCall(
                    () => axios.post(urls.googleLogin, payload),
                    (response) => response.data
                );
                const res = response?.data || response;

                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);

                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res.email);
                    }

                    if (next === "dashboard") {
                        navigate(is4Life ? '/dashboard2' : '/dashboard');;
                    } else if (next === "signup") {
                        setEmail(res.email);
                        storage.set('verifiedEmail', res.email);
                        updateSignupData("email", email);
                        navigate('/signup/account-details');
                    } else {
                        navigate('/login');
                    }

                } else {
                    window?.ReactNativeWebView?.postMessage("googlesignOut");
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }

            } catch (error) {
                console.error("❌ Google login error:", error);
                window.showToast('error', error?.message || screenLabels?.unexpected_error_occurred?.label_value || "Unexpected error occurred");
            }
        };

    }, []);

    useEffect(() => {
        window.loginWithApple = async (token) => {
            let deviceID = storage.get('DeviceId');
            const payload = { deviceID, token, companyKey: companyId, licence_type: licenseType, device_data: getDeviceData() };

            try {
                const response = await handleApiCall(
                    () => axios.post(urls.appleLogin, payload),
                    (response) => response.data
                );
                const res = response?.data || response;

                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);

                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res.email);
                    }

                    if (next === "dashboard") {
                        navigate(is4Life ? '/dashboard2' : '/dashboard');
                    } else if (next === "signup") {
                        setEmail(res.email);
                        storage.set('verifiedEmail', res.email);
                        updateSignupData("email", email);
                        navigate('/signup/account-details');
                    } else {
                        navigate('/login');
                    }

                } else {
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }

            } catch (error) {
                console.error("❌ Google login error:", error);
                window.showToast('error', error?.message || screenLabels?.unexpected_error_occurred?.label_value || "Unexpected error occurred");
            }
        };




        window.loginWith4life = async (token) => {
            const payload = {
                deviceID: deviceId || storage.get('DeviceId'),
                token: token,
                companyKey: companyId,
                device_data: deviceInfo || getDeviceData()
            };
            try {
                const webhookRes = await axios.post(urls.webhookSignup, payload);
                const res = webhookRes?.data;
                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;
                    const isBack = res?.is_back;
                    storage.set('isBack', isBack ? true : false);

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);

                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res?.email);
                    }

                    if (next === "dashboard") {
                        navigate(is4Life ? '/dashboard2' : '/dashboard');
                    } else if (next === "signup") {
                        setEmail(res?.email);
                        storage.set('verifiedEmail', res?.email);
                        updateSignupData("email", res?.email || '');
                        updateSignupData("firstName", res?.first_name || '');
                        updateSignupData("lastName", res?.last_name || '');
                        isBack ? navigate('/welcome-back') : navigate('/signup/account-details');
                    } else {
                        navigate('/login');
                    }

                } else {
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            } catch (error) {
                console.error("❌ 4life login error:", error);
                window.showToast('error', error?.message || screenLabels?.unexpected_error_occurred?.label_value || "Unexpected error occurred");
            }

        };

        console.log("✅ loginWith4life attached to window");

    }, []);

    const createCampaign = useCallback(
        async (campaignData) => {
            if (!deviceId || !userId || !companyId || !campaignData) return;
            const payload = {
                ...campaignData,
                deviceID: deviceId,
                companyKey: companyId,
                userKey: userId,
            }

            await handleApiCall(
                (config) => axios.post(urls.createCampaign, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        window.showToast('success', res?.message);
                        navigate("/invites-list");
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                }
            );
        },
        [deviceId, userId, companyId]
    );

    const getCampaignList = useCallback(async (search = "") => {
        if (!deviceId || !userId || !companyId) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            search: search || "",
        };

        // Typing in the search box refetches silently, so only the initial load
        // shows the overlay - but the increment and decrement have to stay
        // paired either way, or a search would cancel out someone else's loader.
        const showsLoader = search === "";
        if (showsLoader) {
            setIsLoading(true)
        }
        try {
            const freshToken = storage.get("accessToken");
            const config = {
                headers: { Authorization: `Bearer ${freshToken}` }
            };
            const response = await axios.post(urls.listCampaign, payload, config);

            const res = response.data;

            if (res.status !== true) {
                window.showToast(
                    "error",
                    res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                );
                return null;
            }

            // Cache the invitation quota so the list/create screens can warn
            // (and block) when nothing is left, without re-fetching.
            const total =
                Number(res?.total_limit ?? res?.invite_limit ?? res?.limit ?? 0) || 0;
            const used =
                Number(res?.used ?? res?.used_invites ?? res?.used_count ?? 0) || 0;
            const remaining =
                Number(
                    res?.remaining ??
                    res?.remaining_invites ??
                    res?.available ??
                    Math.max(0, total - used)
                ) || 0;
            const guestScanCount = Number(res?.guest_scan_count ?? 0) || 0;
            const inviteScanCount = Number(res?.invite_scan_count ?? 0) || 0;
            setInviteStats({
                total,
                used,
                remaining,
                guestScanCount,
                inviteScanCount,
                loaded: true,
            });

            return res;
        } catch (error) {
            window.showToast(
                "error",
                error?.response?.data?.error ||
                error.message ||
                screenLabels?.something_went_wrong?.label_value ||
                "Something went wrong"
            );
            return null;
        } finally {
            if (showsLoader) setIsLoading(false)
        }
    }, [deviceId, companyId, userId]);

    const getCampaignDetails = useCallback(async (campaignCode) => {
        if (!deviceId || !userId || !companyId || !campaignCode) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            campaignCode: campaignCode
        };

        try {
            const response = await handleApiCall(
                (config) => axios.post(urls.campaignDetails, payload, config),
                (response) => {
                    const res = response.data;

                    if (res.status !== true) {
                        window.showToast(
                            "error",
                            res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                        );
                    }

                    return res;
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error ||
                        error.message ||
                        screenLabels?.something_went_wrong?.label_value ||
                        "Something went wrong"
                    );
                }
            );
            return response || null;
        } catch (error) {
            return null;
        }
    }, [deviceId, companyId, userId]);

    const checkCampaign = useCallback(async (campaignCode) => {
        const devId = deviceId || storage.get('DeviceId');
        if (!devId || !campaignCode) return null;

        const payload = {
            deviceID: devId,
            campaignCode: campaignCode
        };

        try {
            const response = await axios.post(urls.checkCampaign, payload);
            return response?.data || null;
        } catch (error) {
            return null;
        }
    }, [deviceId]);

    const getCampaignScanList = useCallback(async (campaignCode, date = "", page = "") => {
        if (!deviceId || !userId || !companyId || !campaignCode) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            campaignCode: campaignCode,
            page: page,
            date: date
        };

        try {
            const response = await handleApiCall(
                (config) => axios.post(urls.campaignScanList, payload, config),
                (response) => {
                    const res = response.data;

                    if (res.status !== true) {
                        window.showToast(
                            "error",
                            res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                        );
                    }

                    return res;
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error ||
                        error.message ||
                        screenLabels?.something_went_wrong?.label_value ||
                        "Something went wrong"
                    );
                }
            );
            return response || null;
        } catch (error) {
            return null;
        }
    }, [deviceId, companyId, userId]);

    const stopCampaign = useCallback(async (campaignCode) => {
        if (!deviceId || !userId || !companyId || !campaignCode) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            campaignCode: campaignCode
        };

        try {
            const response = await handleApiCall(
                (config) => axios.post(urls.stopCampaign, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        window.showToast(
                            "success",
                            screenLabels?.campaign_expired?.label_value || "Campaign Expired!"
                        );
                    }

                    if (res.status !== true) {
                        window.showToast(
                            "error",
                            res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                        );
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error ||
                        error.message ||
                        screenLabels?.something_went_wrong?.label_value ||
                        "Something went wrong"
                    );
                }
            );
            return response?.data || null;
        } catch (error) {
            return null;
        }
    }, [deviceId, companyId, userId]);

    const saveGuestUser = useCallback(
        async (details) => {
            if (!deviceId || !userId || !companyId) return;

            // let age = "";
            // if (details.dob) {
            //     const today = new Date();
            //     const dob = new Date(details.dob);
            //     age = today.getFullYear() - dob.getFullYear();
            //     const m = today.getMonth() - dob.getMonth();
            //     if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            //         age--;
            //     }
            // }
            if (!details.age || details.age < 18) {
                window.showToast("error", screenLabels?.user_must_be_at_least_18_years_old?.label_value || "User must be at least 18 years old.");
                setMsgLoader(false);
                return;
            }
            let translatedLabels = storage.get('appLabels');


            const labels = {
                // Existing keys                                                                                                               
                pulse_wait: translatedLabels?.common?.pulse_wait,
                no_detect_face: translatedLabels?.common?.no_detect_face,
                no_detect_face_note: translatedLabels?.common?.no_detect_face_note,
                face_detect: translatedLabels?.common?.face_detect,
                scan_comment: translatedLabels?.common?.scan_comment,
                stop: translatedLabels?.common?.stop_scan,
                adjust_face: translatedLabels?.common?.adjust_face,
                scan_stopped_note: translatedLabels?.common?.scan_stopped_note,
                scan_stopped: translatedLabels?.common?.scan_stopped,

                // Top Bar
                app_title: translatedLabels?.common?.app_title,
                front_camera_capture: translatedLabels?.common?.front_camera_capture,
                back_camera_capture: translatedLabels?.common?.back_camera_capture,

                // Oval Label
                position_face: translatedLabels?.common?.position_face,
                locking: translatedLabels?.common?.locking,
                hold_still: translatedLabels?.common?.hold_still,

                // Bottom Status Card
                readiness: translatedLabels?.common?.readiness,
                searching_for_face: translatedLabels?.common?.searching_for_face,
                stabilizing_frame: translatedLabels?.common?.stabilizing_frame,
                motion_detected: translatedLabels?.common?.motion_detected,
                capturing_signal: translatedLabels?.common?.capturing_signal,
                ready_to_begin: translatedLabels?.common?.ready_to_begin,
                locked: translatedLabels?.common?.locked,
                align: translatedLabels?.common?.align,

                // Progress
                of: translatedLabels?.common?.of,
                ready_to_scan: translatedLabels?.common?.ready_to_scan,

                // Buttons
                start_scan: translatedLabels?.common?.start_scan,
                hold_still_btn: translatedLabels?.common?.hold_still_btn,
                locking_face: translatedLabels?.common?.locking_face,
                look_at_camera: translatedLabels?.common?.look_at_camera,

                // Status Messages
                low_light: translatedLabels?.common?.low_light,
                too_bright: translatedLabels?.common?.too_bright,
                move_back: translatedLabels?.common?.move_back,
                move_closer: translatedLabels?.common?.move_closer,
                center_face: translatedLabels?.common?.center_face,
                look_at_camera_msg: translatedLabels?.common?.look_at_camera_msg,
                hold_still_lock: translatedLabels?.common?.hold_still_lock,
                hold_still_msg: translatedLabels?.common?.hold_still_msg,
                face_locked_start: translatedLabels?.common?.face_locked_start,
                hold_phone_eye_level: translatedLabels?.common?.hold_phone_eye_level,

                // Scan Tips
                tip_face_centered: translatedLabels?.common?.tip_face_centered,
                tip_hold_steady: translatedLabels?.common?.tip_hold_steady,
                tip_even_light: translatedLabels?.common?.tip_even_light,
                blink_to_start: translatedLabels?.common?.blink_to_start,
                blink_to_start_hint: translatedLabels?.common?.blink_to_start_hint,
                scanning_hold: translatedLabels?.common?.scanning_hold,
                open_eyes_msg: translatedLabels?.common?.open_eyes_msg,
                // App scan messages (anti-spoof / face cover)
                verify_face: translatedLabels?.common?.verify_face,
                uncover_face: translatedLabels?.common?.uncover_face,
                blink_not_detected: translatedLabels?.common?.blink_not_detected,
                multiple_faces: translatedLabels?.common?.multiple_faces,

                // Web Scan UI (new labels)
                app_title: translatedLabels?.common?.app_title,
                please_wait: translatedLabels?.common?.please_wait,
                low_fps_alert: translatedLabels?.common?.low_fps_alert,
                seconds_unit: translatedLabels?.common?.seconds_unit,
                scan_progress: translatedLabels?.common?.scan_progress,
                live_signal: translatedLabels?.common?.live_signal,
                rppg_label: translatedLabels?.common?.rppg_label,
                scan_stopped: translatedLabels?.common?.scan_stopped,
                camera_permission_denied: translatedLabels?.common?.camera_permission_denied,
                no_camera_found: translatedLabels?.common?.no_camera_found,
                close_scan: translatedLabels?.common?.close_scan,
                mute_voice_guidance: translatedLabels?.common?.mute_voice_guidance,
                unmute_voice_guidance: translatedLabels?.common?.unmute_voice_guidance,

                // Processing Overlay
                generating_health_report: translatedLabels?.common?.generating_health_report,
                analysing_vitals: translatedLabels?.common?.analysing_vitals,

                // Camera Permission Screen
                camera_scan_title: translatedLabels?.common?.camera_scan_title,
                camera_access_needed: translatedLabels?.common?.camera_access_needed,
                camera_permission_text: translatedLabels?.common?.camera_permission_text,
                allow_camera_access: translatedLabels?.common?.allow_camera_access,
                no_camera_device: translatedLabels?.common?.no_camera_device,

                initializing_audio: translatedLabels?.common?.initializing_audio,

                // Low Quality Modal
                unreliable_signal_title: translatedLabels?.common?.unreliable_signal_title,
                unreliable_signal_body: translatedLabels?.common?.unreliable_signal_body,
                unreliable_signal_footer: translatedLabels?.common?.unreliable_signal_footer,
                // min_quality_required: translatedLabels?.common?.min_quality_required,
                scan_again: translatedLabels?.common?.scan_again,

                // Scan Verification / Status
                face_verification_failed_title: translatedLabels?.common?.face_verification_failed_title,
                face_verification_failed_body: translatedLabels?.common?.face_verification_failed_body,
                face_verification_failed_footer: translatedLabels?.common?.face_verification_failed_footer,
                measurement_inconclusive_title: translatedLabels?.common?.measurement_inconclusive_title,
                measurement_inconclusive_body: translatedLabels?.common?.measurement_inconclusive_body,
                measurement_inconclusive_footer: translatedLabels?.common?.measurement_inconclusive_footer,
                scan_timeout_body: translatedLabels?.common?.scan_timeout_body,
                uncover_face: translatedLabels?.common?.uncover_face,
                verify_face: translatedLabels?.common?.verify_face,
                blink_not_detected: translatedLabels?.common?.blink_not_detected,
                multiple_faces: translatedLabels?.common?.multiple_faces,
            };

            const payload = {
                firstName: details.firstName,
                lastName: details.lastName,
                email: details.email,
                deviceID: deviceId,
                parentUserKey: userId,
                guestUserKey: details.guestUserKey,
                companyKey: companyId,
                role: loginObject?.role || 3,
                gender: details.gender,
                weightUnit: details.weightUnit,
                weight: details.weight,
                heightUnit: details.heightUnit,
                waistUnit: details.waistUnit,
                waist_circumference: details.waist_circumference,
                height: details.height,
                smoking: details.smoking,
                dob: details.dob,
                diabetes: details.diabetes,
                hypertension: details.hypertension,
                race: details.race,
                cameraMode: details.cameraMode,
                age: details.age,
                labels: labels,
                servings: details.servings,
                language: storage.get('selectedLanguage') || 'en',
                // Guest scans must NOT inherit the logged-in user's BP
                // calibration baseline — different physiology = different
                // baseline. Tell native to fall through to the demographic-
                // only BP estimate for this scan.
                isGuest: true,
            };

            await handleApiCall(
                (config) => axios.post(urls.saveGuestUser, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        // let isPlanActive = storage.get('isPlanActive');
                        // if (isPlanActive == true) {
                        storage.set("guestUserKey", res?.userData?.guestUserID);
                        storage.set("scanGuest", true);
                        // console.log("=== GuestScan Payload ===");
                        // console.log("Full Payload:", payload);
                        // console.log("Camera Mode:", payload.cameraMode);
                        // console.log("Race:", payload.race);
                        // console.log("Gender:", payload.gender);
                        // console.log("Age:", payload.age);
                        // console.log("=======================");
                        // console.log(payload);

                        window.ReactNativeWebView?.postMessage(`startGuestScan|${JSON.stringify(payload)}`);
                        // } else {
                        //     setMsgLoader(false);
                        //     navigate('/plan-expired');
                        // }
                    } else {
                        // Scan couldn't start — let the guideline modal reappear next
                        // time so the user gets a fresh chance to review it.
                        resetGuidelines('scanGuidelinesSeen');
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                        setMsgLoader(false);
                        navigate('/plan-expired');
                    }
                },
                (error) => {
                    resetGuidelines('scanGuidelinesSeen');
                    setMsgLoader(false);
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error?.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                }
            );
        },
        [deviceId, userId, companyId, isPlanActive]
    );

    const getGuestUserList = useCallback(async (shouldNavigate = true) => {
        if (!deviceId || !userId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3
        };

        await handleApiCall(
            (config) => axios.post(urls.getGuestUserList, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setGuestList(res.list || []);
                    if (!shouldNavigate) return true;

                    if (res.list && res.list.length > 0) {
                        navigate('/guest-list');
                    } else {
                        // No guests yet — heading straight to a fresh guest
                        // form, so drop any leftover key from an older guest.
                        storage.remove('guestUserKey');
                        navigate('/guest-scan');
                    }
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    if (shouldNavigate) {
                        storage.remove('guestUserKey');
                        navigate('/guest-scan');
                    }
                    return false;
                }
            },
            (error) => {
                window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                if (shouldNavigate) {
                    storage.remove('guestUserKey');
                    navigate('/guest-scan');
                }
                return false;
            }
        );
    }, [deviceId, companyId, userId, navigate]);

    const getGuestUserDetails = useCallback(async (guestUserKey) => {
        if (!deviceId || !userId || !companyId || !guestUserKey) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            guestUserKey: guestUserKey,
            role: loginObject?.role || 3
        };

        return await handleApiCall(
            (config) => axios.post(urls.getGuestUserDetails, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    return res.detail;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return null;
                }
            },
            (error) => {
                window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                return null;
            }
        );
    }, [deviceId, companyId, userId]);

    const deleteGuestUser = useCallback(async (guestUserKey) => {
        if (!deviceId || !userId || !guestUserKey) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            guestUserKey: guestUserKey,
            role: loginObject?.role || 3
        };

        const result = await handleApiCall(
            (config) => axios.post(urls.deleteGuestUser, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast("success", res.message || screenLabels?.guest_user_deleted_successfully?.label_value || "Guest user deleted successfully");
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return false;
                }
            },
            (error) => {
                window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                return false;
            }
        );
        if (result) {
            await getGuestUserList(false);
        }
        return result;
    }, [deviceId, userId, getGuestUserList, loginObject?.role]);

    const getUserScanLimit = useCallback(async (scan_type) => {
        if (!deviceId || !companyId || !userId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            ...(scan_type ? { scan_type } : {}),
        };

        const cacheKey = `cachedScanLimit_${userId}`;
        const cached = storage.get(cacheKey);
        if (cached !== null && cached !== undefined) {
            setScanLimit(cached);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.getScanLimit, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                setScanLimit(res?.remain_status);
                storage.set(cacheKey, res?.remain_status);
            } else if (cached === null || cached === undefined) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (cached === null || cached === undefined) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, companyId, userId, loginObject]);

    const updateUserServings = useCallback(async (serving) => {
        if (!deviceId || !companyId || !userId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            fruits: serving.fruits,
            vegetables: serving.vegetables,
            grains: serving.grains,
            sugaryfoods: serving.sugaryfoods,
            redmeat: serving.redmeat,
            fish: serving.fish,
            activity: serving.activity
        };

        await handleApiCall(
            (config) => axios.post(urls.updateServings, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', res.message);
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, companyId, userId]);


    const fourLifeUserLogin = useCallback(async () => {
        if (!deviceId) return;

        const payload = {
            deviceID: deviceId,
            company_id: process.env.REACT_APP_COMPANY_ID,
            email: fourLifeuserData.userName,
            password: fourLifeuserData.password,
            licence_type: licenseType,
        };

        if (!fourLifeuserData.userName) {
            window.showToast("error", screenLabels?.please_enter_your_username?.label_value || "Please enter your username.");
            return;
        }

        if (!fourLifeuserData.password) {
            window.showToast("error", screenLabels?.please_enter_your_password?.label_value || "Please enter your password.");
            return;
        }
        await handleApiCall(
            () => axios.post(urls.userLogin, payload),
            (response) => {
                const res = response.data;
                if (res?.status === true) {
                    if (res?.next_screen == "signup") {
                        navigate('/signup/account-details');
                        return;
                    }
                    storage.set('isLoggedIn', true)
                    const decodeAccessToken = jwtDecode(res?.token?.access_token);

                    storage.set('accessToken', res?.token?.access_token);
                    storage.set('refreshToken', res?.token?.refresh_token);
                    storage.set('tokenExpiry', decodeAccessToken?.exp);
                    startTokenRefreshTimer();
                    storage.set('loginObj', decodeAccessToken)
                    storage.remove('verifiedEmail');
                    let navigatetodashboard = is4Life ? '/dashboard2' : '/dashboard';
                    navigate(navigatetodashboard)
                    storage.set('UserModule', 'Login')
                    let userId = decodeAccessToken?.userKey;
                    window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                    updateUserData('password', '')
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('password', '')
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            false
        );
    }, [deviceId, companyId, fourLifeuserData]);


    const getFourLifeScanHistory = useCallback(async () => {
        if (!deviceId) return;
        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
        };


        await handleApiCall(
            (config) => axios.post(urls.getFourLifeScanHistory, payload, config, { silent: true }),
            (response) => {
                const res = response.data;
                if (res?.status === true) {
                    setFourLifeScanHistory(res?.list || [])
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('password', '')
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, companyId, fourLifeuserData, userId]);

    useEffect(() => {
        updateUserLanguage();
    }, [selected, updateUserLanguage]);

    // ─── Blood-pressure calibration backend sync ───────────────────────────
    // We persist the per-axis averaged diff (cuff − uncalibrated scan). To
    // apply, native adds this diff back onto each new raw scan, giving a
    // per-user offset anchored to that user's own cuff.
    const saveBPCalibration = useCallback(async (baseline) => {
        if (!deviceId || !userId || !baseline) return null;
        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            systolicDiff: Math.round(baseline.systolicDiff),
            diastolicDiff: Math.round(baseline.diastolicDiff),
            pulseDiff: Math.round(baseline.pulseDiff),
            calibratedAt: baseline.calibratedAt,
        };
        return await handleApiCall(
            (config) => axios.post(urls.saveBPCalibration, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    console.log('🔵 BP calibration diff synced to backend', payload);
                    return res?.data;
                }
                if (res?.error) window.showToast('error', res.error);
                return null;
            },
        );
    }, [deviceId, userId, companyId]);

    const getBPCalibration = useCallback(async () => {
        if (!deviceId || !userId) return null;
        return await handleApiCall(
            (config) => axios.post(
                urls.getBPCalibration,
                { deviceID: deviceId, userKey: userId, companyKey: companyId },
                config,
            ),
            (response) => {
                // Backend returns snake_case fields. Normalize to the camelCase
                // shape the rest of the app expects, so every consumer can read
                // `systolicDiff`, `calibratedAt`, etc. without caring about the
                // wire format.
                const raw = response?.data?.data;
                if (!raw) return null;
                const rawDate = raw.calibratedAt ?? raw.calibrated_at;
                if (!rawDate) return null;
                // Backend may return "YYYY-MM-DD HH:mm:ss". Convert to a parseable
                // ISO-ish form so Safari does not return Invalid Date.
                const calibratedAt = typeof rawDate === 'string'
                    ? rawDate.replace(' ', 'T')
                    : rawDate;
                return {
                    systolicDiff: Number(raw.systolicDiff ?? raw.systolic_diff ?? 0),
                    diastolicDiff: Number(raw.diastolicDiff ?? raw.diastolic_diff ?? 0),
                    pulseDiff: Number(raw.pulseDiff ?? raw.pulse_diff ?? 0),
                    calibratedAt,
                    pointCount: Number(raw.pointCount ?? raw.point_count ?? 0),
                };
            },
        );
    }, [deviceId, userId, companyId]);

    const deleteBPCalibration = useCallback(async () => {
        if (!deviceId || !userId) return null;
        return await handleApiCall(
            (config) => axios.post(
                urls.deleteBPCalibration,
                { deviceID: deviceId, userKey: userId, companyKey: companyId },
                config,
            ),
            (response) => response?.data?.status === true,
        );
    }, [deviceId, userId, companyId]);

    // Pull the latest baseline from the backend and push it into the native
    // RAM cache via `hydrateCalibration`. Native is in-memory only, so calling
    // this immediately before a scan guarantees the freshest baseline (e.g. if
    // the user calibrated on another device since this session started).
    const refreshCalibration = useCallback(async () => {
        if (!userId) return null;
        const bridge = typeof window !== 'undefined' ? window.ReactNativeWebView : null;
        try {
            const data = await getBPCalibration();
            // console.log("[BP hydrate] GET response:", data);
            if (!data || !data.calibratedAt) {
                bridge?.postMessage?.(JSON.stringify({
                    type: 'hydrateCalibration',
                    payload: { userId, baseline: null },
                }));
                return null;
            }
            const baseline = {
                systolicDiff: Number(data.systolicDiff) || 0,
                diastolicDiff: Number(data.diastolicDiff) || 0,
                pulseDiff: Number(data.pulseDiff) || 0,
                calibratedAt: data.calibratedAt || new Date().toISOString(),
                pointCount: Number(data.pointCount) || 0,
            };
            bridge?.postMessage?.(JSON.stringify({
                type: 'hydrateCalibration',
                payload: { userId, baseline },
            }));
            // console.log('🔵 BP calibration hydrated to native:', baseline);
            return baseline;
        } catch (e) {
            console.warn('[BP] hydrate from backend failed', e);
            return null;
        }
    }, [userId, getBPCalibration]);

    // Hydrate once on login / user switch. Subsequent scans also re-hydrate
    // via `refreshCalibration` right before posting `startScan` — that catches
    // the cross-device case where another device calibrated mid-session.
    useEffect(() => {
        if (!userId) return;
        const bridge = typeof window !== 'undefined' ? window.ReactNativeWebView : null;
        if (!bridge?.postMessage) return;
        let cancelled = false;
        (async () => {
            const result = await refreshCalibration();
            if (cancelled) return;
            void result;
        })();
        return () => { cancelled = true; };
    }, [userId, refreshCalibration]);

    if (msgLoader) {
        return <Loader />
    }

    // Dynamic copy for each scan-issue popup variant, keyed off `scanIssueModal`.
    // Both variants share the same ScanIssueModal UI (see render below).
    const commonLabels = translatedLabels?.common;
    const SCAN_ISSUE_CONTENT = {
        measurementInconclusive: {
            title: commonLabels?.measurement_inconclusive_title?.label_value || "Measurement Inconclusive",
            body: commonLabels?.measurement_inconclusive_body?.label_value || "There wasn't enough clear pulse signal to produce reliable results. No measurements were saved.",
            footer: commonLabels?.measurement_inconclusive_footer?.label_value || "Please try again when you're comfortable and the lighting is steady.",
        },
        lowQuality: {
            title: commonLabels?.unreliable_signal_title?.label_value || "Unreliable Signal",
            body: commonLabels?.unreliable_signal_body?.label_value || "We couldn't get a reliable signal from your face, so we can't continue the face scan. Please try again.",
            footer: commonLabels?.unreliable_signal_footer?.label_value || "If you see this again, try in normal room lighting and avoid having bright light directly behind you",
        },
        verificationFailed: {
            title: commonLabels?.face_verification_failed_title?.label_value || "Face Verification Unsuccessful",
            body: commonLabels?.face_verification_failed_body?.label_value || "We couldn't confirm a live face for this scan. No measurements were saved.",
            footer: commonLabels?.face_verification_failed_footer?.label_value || "Please scan your face directly, without a photo or screen in front of the camera.",
        },
        timeout: {
            title: commonLabels?.scan_timeout_title?.label_value || "No Face Detected",
            body: commonLabels?.scan_timeout_body?.label_value || "No face was detected, so we can't continue the face scan. Please try again.",
            footer: commonLabels?.scan_timeout_footer?.label_value || "Make sure your face is clearly visible and centered in the frame before scanning.",
        },
        blinkNotDetected: {
            title: commonLabels?.blink_not_detected_title?.label_value || "No Blink Detected",
            body: commonLabels?.blink_not_detected_body?.label_value || "We couldn't detect a blink during your scan. This helps us confirm you're ready for an accurate reading",
            footer: commonLabels?.blink_not_detected_footer?.label_value || "Please look directly at the camera and blink naturally when prompted.",
        },
    };
    const scanIssueContent = SCAN_ISSUE_CONTENT[scanIssueModal];
    return (
        <GlobalContext.Provider
            value={{
                isLoading,
                showPasswords,
                togglePassword,
                updateUserData,
                userData,
                checkUserRegistration,
                email,
                loginByPassword,
                handlenavigateToEmail,
                sendEmailOTP,
                verifyOTP,
                setEmail,
                doLogout,
                resetPassword,
                resendEmailOTP,
                timer,
                resendDisabled,
                updateSignupData,
                createAccountScreenOne,
                createAccountScreenTwo,
                createAccountScreenThree,
                createAccountScreenFour,
                translatedLabels,
                setSelected,
                selected,
                setIsManualChange,
                translateData,
                signupData,
                completeSignup,
                setSignupData,
                getUserDetails,
                userDetails,
                updateUserDetails,
                isVerified,
                setIsVerified,
                deleteUserAcc,
                handleChangePassword,
                getFaqList,
                faqList,
                uploadCropImg,
                selectedBillType,
                setSelectedBillype,
                getLanguageList,
                languages,
                logoUrl,
                headerLogoUrl,
                uploadDocument,
                documentData,
                setDocumentData,
                getDocumentList,
                uploadedFiles,
                getTermsConditions,
                documentContent,
                getPrivacypolicy,
                getScanDetails,
                scanDetails,
                topSection,
                getArticlesList,
                articlesList,
                pagination,
                getArticleDetailsByID,
                articleDetails,
                getScanHistory,
                scanHistory,
                deletedocument,
                getWellnessCat,
                wellnessCat,
                listBioMarker,
                bioMarker,
                editUserDetails,
                tempScanDetails,
                setMsgLoader,
                scanIssueModal,
                setScanIssueModal,
                showExportModal,
                setShowExportModal,
                userEmail,
                showCalendar,
                setShowCalendar,
                calendarRef,
                exportScanDetails,
                globalUserEmail,
                setGlobalUserEmail,
                getHistoryDates,
                historyDates,
                setSearch,
                search,
                resetArticlesSearch,
                wellnessLabel,
                wellnessLabelNormal,
                getNotificationList,
                getScanLogList,
                notification,
                readNotifications,
                hasNotification,
                setHasNotification,
                isNotificationReceived,
                setIsNotificationReceived,
                handleNotificationClick,
                getTimeZone,
                getVoiceAudioFiles,
                timeZone,
                notificationOnOff,
                setNotificationsEnabled,
                notificationsEnabled,
                handleDeletAccount,
                showOTPSection,
                setShowOTPSection,
                getuserDetails,
                setUserDatav2,
                userDatav2,
                verifyAccessCode,
                isThankYou,
                createCampaign,
                getCampaignList,
                inviteStats,
                getCampaignDetails,
                getCampaignScanList,
                checkCampaign,
                stopCampaign,
                saveGuestUser,
                getGuestUserList,
                getGuestUserDetails,
                deleteGuestUser,
                guestList,
                getUserScanLimit,
                scanLimit,
                setScanLimit,
                is4Life,
                servings,
                updateUserServings,
                setFourLifeUserData,
                fourLifeuserData,
                fourLifeUserLogin,
                fourLifeSSOLogin,
                products,
                isProfileUpdated,
                fetchFreshProducts,
                dynamicTypes,
                getRecProductList,
                fullProducts,
                redirection,
                fourLifeVerifyOTP,
                fourLifeEmailOTP,
                fourLiferesendOTP,
                verifiedsecEmail,
                deleteGuestUser,
                getFourLifeScanHistory,
                fourLifeScanHistory,
                saveBPCalibration,
                getBPCalibration,
                deleteBPCalibration,
                refreshCalibration,
                scanSpectrum,
                scanSpectrumV2
            }}
        >
            {isLoading && (
                <Loader />
            )}
            <ScanIssueModal
                show={!!scanIssueModal}
                onClose={() => setScanIssueModal(null)}
                onRetry={() => setScanIssueModal(null)}
                title={scanIssueContent?.title}
                body={scanIssueContent?.body}
                footer={scanIssueContent?.footer}
                buttonText={commonLabels?.scan_again?.label_value || "Scan Again"}
            />
            {children}
        </GlobalContext.Provider>
    );
};
